// TCIO Bed Board — facelift rewrite 2026-09-01 (brief ratified in-session)
// Facelift only: every action/field/modal/workflow from the previous board survives.
// Sanctioned API changes ride commit 53faba1 (dollar gate, pending clamp, house_type).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { tokens as tk } from '../theme';
import {
  BedDouble, DoorOpen, UserPlus, CalendarClock, ShieldAlert, ArrowLeftRight,
  RefreshCw, Search, ChevronDown, ChevronRight, MoreHorizontal, LayoutGrid,
  List as ListIcon, Building2, LogOut, Move,
} from 'lucide-react';

// Ticket 892c7392 (Jaclyn 9/23): names are stored "Last, First" - a typed "First Last" never matched. Every typed word must appear, any order.
const wordsMatch = (hay, q) => { const h = String(hay || '').toLowerCase().replace(/,/g, ' '); return String(q || '').toLowerCase().split(/\s+/).filter(Boolean).every(w => h.includes(w)); };

const API = 'https://tcio-api.onrender.com';

// Glyph consts (paste-mangling doctrine: never raw non-ASCII)
const DOT = '\u00B7';
const CHECK = '\u2713';
const DASH = '\u2014';
const ELLIP = '\u2026';
const TIMES = '\u2715';
const ARROW = '\u2192';
const WARN = '\u26A0';
const PENCIL = '\u270E';
const MOVEG = '\u21A6';
const SWAP = '\u21C4';

const IC = { size: 18, strokeWidth: 1.75 };
const ICS = { size: 16, strokeWidth: 1.75 };

// House ordering comes from houses.sort_order (server field house_sort_order) - zero-hardcode doctrine.
const LOCS = ['DTX','RTC','PHP','IOP'];
const COORDINATORS = ['Andrew','Aurora','Jaclyn','Sam','Taryn','Tiffani','Zac',''];
const SOURCES = ['Google Ads','Meta Ads','Alumni','Organic','Referral Partner','Other','']; // Generic only - tenant-specific sources belong to the CRM lead_sources list (tenanted 2026-09-07)
const DISCHARGE_REASONS = ['Completed','AMA','ACA','Administrative','Transferred','No Show','Other'];

// LOC identity colors — theme tokens, LOC identity ONLY (brief section 5)
const LOC_TK = {
  DTX: { c: tk.locDtx, soft: tk.locDtxSoft },
  RTC: { c: tk.locRtc, soft: tk.locRtcSoft },
  PHP: { c: tk.locPhp, soft: tk.locPhpSoft },
  IOP: { c: tk.locIop, soft: tk.locIopSoft },
};
const locC = (loc) => LOC_TK[loc] || { c: tk.textMuted, soft: tk.surfaceSoft };

// Auth status chips — semantic tokens (red = failure, amber = attention, blue = normal workflow)
const AUTH_BADGE = {
  MISSED:      { label: 'NO AUTH',  bg: tk.danger },
  EXPIRED:     { label: 'EXPIRED',  bg: tk.danger },
  NO_DATES:    { label: 'NO DATES', bg: tk.warning },
  PAYER_STALL: { label: 'PAYER',    bg: tk.warning },
  EXPIRING:    { label: 'EXPIRING', bg: tk.warning },
  PENDING:     { label: 'PENDING',  bg: tk.ai },
};
const ACTIONABLE = ['MISSED','EXPIRED','PENDING_SLOW','NO_DATES','PAYER_STALL','POST_DC'];

// ── Date helpers: Pacific-safe date-only math, carried verbatim ──
function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.floor((now - d) / 86400000);
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const s = (dateStr instanceof Date ? dateStr.toISOString() : String(dateStr)).slice(0, 10);
  const dMs = Date.UTC(+s.slice(0,4), +s.slice(5,7) - 1, +s.slice(8,10));
  const n = new Date();
  const nowMs = Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
  return Math.floor((dMs - nowMs) / 86400000);
}
function formatDate(dateStr) {
  if (!dateStr) return '';
  const s = (dateStr instanceof Date ? dateStr.toISOString() : String(dateStr)).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${+m[2]}/${+m[3]}/${m[1]}`;
  const d = new Date(dateStr);
  return `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`;
}
function toInputDate(dateStr) {
  if (!dateStr) return '';
  const s = (dateStr instanceof Date ? dateStr.toISOString() : String(dateStr));
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : new Date(dateStr).toISOString().slice(0,10);
}
function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 700);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ── Shared styles on tokens ──
const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: tk.surface, borderRadius: tk.radiusCard, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(10,34,57,0.25)', fontFamily: tk.font },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${tk.borderSoft}`, position: 'sticky', top: 0, background: tk.surface, zIndex: 1 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 600, color: tk.ink },
  closeBtn: { background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: tk.textMuted, padding: 4 },
  modalBody: { padding: '20px 22px 24px' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  fieldLabel: { display: 'block', fontSize: 11, fontWeight: 600, color: tk.textSecondary, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { width: '100%', padding: '9px 11px', border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', color: tk.ink, background: tk.surface },
  btnRow: { display: 'flex', gap: 8, marginTop: 16 },
  btnPrimary: { flex: 1, padding: '11px 16px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnBlue: { flex: 1, padding: '11px 16px', background: tk.surface, color: tk.blue700, border: `1.5px solid ${tk.blue600}`, borderRadius: tk.radiusControl, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { flex: 1, padding: '11px 16px', background: 'transparent', color: tk.textSecondary, border: `1.5px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGreen: { flex: 1, padding: '11px 16px', background: tk.success, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnRed: { flex: 1, padding: '11px 16px', background: tk.danger, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  errBox: { background: tk.dangerBg, border: `1px solid ${tk.danger}`, borderRadius: tk.radiusControl, padding: '10px 14px', color: tk.danger, fontSize: 13, marginBottom: 12 },
  summaryBox: { background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: '12px 16px', marginBottom: 14 },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', borderBottom: `1px solid ${tk.borderSoft}`, color: tk.ink },
  secTitle: { fontSize: 11, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 },
  select: { padding: '8px 12px', borderRadius: tk.radiusControl, border: `1px solid ${tk.border}`, fontSize: 13, fontWeight: 600, color: tk.ink, cursor: 'pointer', background: tk.surface, fontFamily: 'inherit' },
};

// ── Canonical payer dropdown (verbatim: datalist search + deliberate add-new) ──
let PAYER_CACHE = null;
function PayerSelect({ value, onChange, inputStyle }) {
  const [list, setList] = useState(PAYER_CACHE || []);
  useEffect(() => {
    if (PAYER_CACHE) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/payers/canonical`, { headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } });
        const j = await r.json();
        if (j.ok) { PAYER_CACHE = j.payers; setList(j.payers); }
      } catch (e) {}
    })();
  }, []);
  const addNew = async () => {
    const name = window.prompt('New payer name (exactly as the payer bills, e.g. BCBS OF NEVADA):');
    if (!name || name.trim().length < 3) return;
    try {
      const r = await fetch(`${API}/api/payers/canonical`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      const j = await r.json();
      if (j.ok) {
        PAYER_CACHE = [...(PAYER_CACHE || []), j.name];
        setList(PAYER_CACHE);
        onChange(j.name);
      } else window.alert(j.error || 'Failed to add payer');
    } catch (e) { window.alert('Network error'); }
  };
  const sel = (v) => { if (v === '__ADD__') addNew(); else onChange(v); };
  return (
    <select style={inputStyle} value={list.includes(value) ? value : (value ? '__CURRENT__' : '')} onChange={e => sel(e.target.value)}>
      <option value="">{DASH}</option>
      {value && !list.includes(value) && <option value="__CURRENT__">{value}</option>}
      {list.map(p => <option key={p} value={p}>{p}</option>)}
      <option value="__ADD__">+ Add new payer{ELLIP}</option>
    </select>
  );
}

// ── Staff dropdowns backed by /api/lookups (verbatim) ──
let STAFF_CACHE = null;
function useStaffLists() {
  const [lists, setLists] = useState(STAFF_CACHE || { therapists: [], case_managers: [] });
  useEffect(() => {
    if (STAFF_CACHE) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/lookups`, { headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } });
        const j = await r.json();
        if (j.ok) {
          STAFF_CACHE = { therapists: j.therapists || [], case_managers: j.case_managers || [] };
          setLists(STAFF_CACHE);
        }
      } catch (e) {}
    })();
  }, []);
  return lists;
}
function StaffSelect({ kind, value, onChange, inputStyle, facilityId }) {
  const lists = useStaffLists();
  const rows = kind === 'therapist' ? lists.therapists : lists.case_managers;
  const seen = new Set();
  const list = [];
  rows.forEach(s => {
    if (facilityId && Array.isArray(s.facility_ids) && !s.facility_ids.map(String).includes(String(facilityId))) return;
    const n = ((s.first_name || '') + ' ' + (s.last_name || '')).trim();
    if (n && !seen.has(n)) { seen.add(n); list.push(n); }
  });
  return (
    <select style={inputStyle} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{DASH}</option>
      {value && !list.includes(value) && <option value={value}>{value}</option>}
      {list.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{title}</h3>
          <button onClick={onClose} style={styles.closeBtn}>{TIMES}</button>
        </div>
        <div style={styles.modalBody}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}
const AgeChip = ({ dob }) => { if (!dob) return null; const a = Math.floor((Date.now() - new Date(dob)) / 31557600000); return isNaN(a) ? null : <span style={{ marginLeft: 8, background: tk.aiSoft, color: tk.blue700, borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>Age {a}</span>; };

// ── Facelift primitives ──
function StatTile({ icon, label, value, sub, subColor, accent, onClick, active }) {
  return (
    <div onClick={onClick} style={{ background: tk.surface, border: `1px solid ${active ? tk.ai : tk.borderSoft}`, borderRadius: tk.radiusCard, padding: '10px 14px', minWidth: 108, cursor: onClick ? 'pointer' : 'default', boxShadow: tk.shadow, transition: 'border-color 160ms' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        {icon}
        <span style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent || tk.ink, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, fontWeight: 600, color: subColor || tk.textSecondary, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}
function OccBar({ occ, total, color }) {
  const pct = total ? Math.round((occ / total) * 100) : 0;
  return (
    <div style={{ flex: 1, height: 4, background: tk.surfaceSoft, borderRadius: 2, overflow: 'hidden', minWidth: 60 }}>
      <div style={{ width: pct + '%', height: '100%', background: color || tk.blue600, transition: 'width 200ms' }} />
    </div>
  );
}
function LocChip({ loc }) {
  const c = locC(loc);
  return <span style={{ fontSize: 10, fontWeight: 700, background: c.c, color: '#fff', padding: '1px 7px', borderRadius: 8, letterSpacing: '0.03em' }}>{loc}</span>;
}

// ─── PENDING ADMIT FORM (fields + endpoints verbatim; tokens only) ───
function PendingAdmitModal({ facilityId, editData, onClose, onSuccess }) {
  const [form, setForm] = useState({
    patient_name: editData?.patient_name || '',
    payer: editData?.payer || '',
    member_id: editData?.member_id || '',
    loc: editData?.loc || 'DTX',
    gender: editData?.gender || '',
    dob: toInputDate(editData?.dob) || '',
    coordinator: editData?.coordinator || '',
    referral_source: editData?.referral_source || '',
    expected_admit_date: toInputDate(editData?.expected_admit_date) || '',
    auth_number: editData?.auth_number || '',
    auth_thru: toInputDate(editData?.auth_thru) || '',
    notes: editData?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.patient_name.trim()) { setError('Patient name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (editData) await axios.patch(`${API}/api/pending-admits/${editData.id}`, form);
      else await axios.post(`${API}/api/pending-admits`, { ...form, facility_id: facilityId });
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error saving.'); }
    setSaving(false);
  };
  return (
    <Modal title={editData ? `Edit ${DASH} ${editData.patient_name}` : 'Add Pending Admit'} onClose={onClose}>
      {error && <div style={styles.errBox}>{error}</div>}
      <div style={styles.grid2}>
        <Field label="Patient Name *"><input style={styles.input} value={form.patient_name} onChange={e => set('patient_name', e.target.value)} placeholder="First Last" /></Field>
        <Field label="Gender"><select style={styles.input} value={form.gender} onChange={e => set('gender', e.target.value)}><option value="">{DASH}</option><option value="M">Male</option><option value="F">Female</option></select></Field>
        <Field label="Payer"><PayerSelect inputStyle={styles.input} value={form.payer} onChange={v => set('payer', v)} /></Field>
        <Field label="Level of Care"><select style={styles.input} value={form.loc} onChange={e => set('loc', e.target.value)}>{LOCS.map(l => <option key={l}>{l}</option>)}</select></Field>
        <Field label="Member ID"><input style={styles.input} value={form.member_id} onChange={e => set('member_id', e.target.value)} /></Field>
        <Field label="Date of Birth"><input type="date" style={styles.input} value={form.dob} onChange={e => set('dob', e.target.value)} /></Field>
        <Field label="Coordinator"><select style={styles.input} value={form.coordinator} onChange={e => set('coordinator', e.target.value)}><option value="">{DASH}</option>{COORDINATORS.map(c => <option key={c} value={c}>{c || DASH}</option>)}</select></Field>
        <Field label="Referral Source"><select style={styles.input} value={form.referral_source} onChange={e => set('referral_source', e.target.value)}><option value="">{DASH}</option>{SOURCES.map(s => <option key={s} value={s}>{s || DASH}</option>)}</select></Field>
        <Field label="Expected Admit Date"><input type="date" style={styles.input} value={form.expected_admit_date} onChange={e => set('expected_admit_date', e.target.value)} /></Field>
        <Field label="Auth Number"><input style={styles.input} value={form.auth_number} onChange={e => set('auth_number', e.target.value)} /></Field>
        <Field label="Auth Approved Thru"><input type="date" style={styles.input} value={form.auth_thru} onChange={e => set('auth_thru', e.target.value)} /></Field>
      </div>
      <Field label="Notes"><textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
      <div style={styles.btnRow}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={submit} disabled={saving}>{saving ? 'Saving' + ELLIP : editData ? 'Save Changes' : 'Add to Queue'}</button>
      </div>
    </Modal>
  );
}

// ─── ASSIGN BED MODAL (verbatim logic; tokens only) ───
function AssignBedModal({ pending, beds, facilityId, onClose, onSuccess }) {
  const availableBeds = beds.filter(b => !b.patient_name && b.bed_type !== 'staff');
  const locBeds = availableBeds.filter(b => b.loc === pending.loc);
  const otherBeds = availableBeds.filter(b => b.loc !== pending.loc);
  const [selectedBed, setSelectedBed] = useState(locBeds[0]?.bed_code || otherBeds[0]?.bed_code || '');
  const [admitDate, setAdmitDate] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  });
  const [therapist, setTherapist] = useState('');
  const [caseManager, setCaseManager] = useState('');
  const [loc, setLoc] = useState(pending.loc || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!selectedBed) { setError('Please select a bed.'); return; }
    if (!loc) { setError('Select the level of care to attempt authorization for.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/pending-admits/${pending.id}/admit`, {
        bed_id: selectedBed, admit_date: admitDate, loc,
        therapist: therapist || null, case_manager: caseManager || null,
      });
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error admitting.'); }
    setSaving(false);
  };
  return (
    <Modal title={`Assign Bed ${DASH} ${pending.patient_name}`} onClose={onClose}>
      {error && <div style={styles.errBox}>{error}</div>}
      <div style={styles.summaryBox}>
        <div style={styles.summaryRow}><span>Patient</span><b>{pending.patient_name}</b></div>
        <div style={styles.summaryRow}><span>Payer</span><b>{pending.payer || DASH}</b></div>
        <div style={styles.summaryRow}><span>LOC to attempt</span>
          <select style={{ ...styles.input, width: 'auto', padding: '4px 8px', fontWeight: 700 }} value={loc} onChange={e => setLoc(e.target.value)}>
            <option value="">Select...</option>
            {LOCS.map(l => <option key={l} value={l}>{l}</option>)}
          </select></div>
        <div style={styles.summaryRow}><span>Gender</span><b>{pending.gender || DASH}</b></div>
        {pending.auth_number && <div style={styles.summaryRow}><span>Auth #</span><b>{pending.auth_number}</b></div>}
      </div>
      <Field label="Admit Date"><input type="date" style={styles.input} value={admitDate} onChange={e => setAdmitDate(e.target.value)} /></Field>
      <Field label={`Select Bed (${availableBeds.length} open)`}>
        <select style={styles.input} value={selectedBed} onChange={e => setSelectedBed(e.target.value)}>
          <option value="">{DASH} Select a bed {DASH}</option>
          {locBeds.length > 0 && (
            <optgroup label={`${pending.loc} Beds (recommended)`}>
              {locBeds.map(b => <option key={b.bed_code} value={b.bed_code}>{b.bed_code} {DASH} {b.house_name} ({b.loc})</option>)}
            </optgroup>
          )}
          {otherBeds.length > 0 && (
            <optgroup label="Other Available Beds">
              {otherBeds.map(b => <option key={b.bed_code} value={b.bed_code}>{b.bed_code} {DASH} {b.house_name} ({b.loc})</option>)}
            </optgroup>
          )}
        </select>
      </Field>
      <div style={styles.grid2}>
        <Field label="Therapist"><StaffSelect kind="therapist" facilityId={facilityId} inputStyle={styles.input} value={therapist} onChange={setTherapist} /></Field>
        <Field label="Case Manager"><StaffSelect kind="case_manager" facilityId={facilityId} inputStyle={styles.input} value={caseManager} onChange={setCaseManager} /></Field>
      </div>
      <div style={styles.btnRow}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.btnGreen, opacity: saving ? 0.6 : 1 }} onClick={submit} disabled={saving}>{saving ? 'Admitting' + ELLIP : CHECK + ' Confirm Admit'}</button>
      </div>
    </Modal>
  );
}

// ─── PENDING ADMITS — dense queue (brief sections 14-19) ───
// Every legacy action survives: collapsed row = Assign Bed + expand; the full set
// (Edit / Move board / Hold-Release / Request Prescreen / +Lead / Kipu / Remove)
// lives in the inline expansion. Progressive disclosure, nothing removed.
const paBtn = { padding: '6px 11px', background: tk.surface, color: tk.blue700, border: `1px solid ${tk.blue600}`, borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' };
const paBtnDanger = { ...paBtn, color: tk.danger, border: `1px solid ${tk.danger}` };

function PendingAdmitsSection({ facilityId, emrLive, beds, onRefresh, canSeeDollars }) {
  const isMobile = useIsMobile();
  const [pendingAdmits, setPendingAdmits] = useState([]);
  const [paSearch, setPaSearch] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [subFilter, setSubFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [assignItem, setAssignItem] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [moveItem, setMoveItem] = useState(null);
  const [moveOrgs, setMoveOrgs] = useState([]);
  const [holdItem, setHoldItem] = useState(null);

  useEffect(() => {
    if (!moveItem || moveOrgs.length) return;
    axios.get(`${API}/api/organizations`)
      .then(r => setMoveOrgs((r.data.data || []).filter(o => o.active && !o.hidden && o.org_type === 'facility')))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moveItem]);

  const fetchPending = useCallback(async () => {
    if (!facilityId) return;
    try {
      const res = await axios.get(`${API}/api/pending-admits?facilityId=${facilityId}`);
      setPendingAdmits(res.data.data || []);
    } catch (e) { console.error(e); }
  }, [facilityId]);
  useEffect(() => { fetchPending(); }, [fetchPending]);
  // A bed action can move a pending card (Never Admitted returns one to this list): reload when the bed side refreshes, no page reload needed.
  useEffect(() => { const h = () => fetchPending(); window.addEventListener('tcio:pending-changed', h); return () => window.removeEventListener('tcio:pending-changed', h); }, [fetchPending]);

  const moveTo = async (org) => {
    if (!window.confirm('Move ' + moveItem.patient_name + ' to ' + (org.display_name || org.org_name) + "'s bed board?")) return;
    try {
      await axios.patch(`${API}/api/pending-admits/${moveItem.id}`, { facility_id: org.id });
      setMoveItem(null); fetchPending();
      alert(moveItem.patient_name + ' is now on ' + (org.display_name || org.org_name) + "'s bed board.");
    } catch (e) { alert(e.response?.data?.error || 'Move failed'); }
  };
  const holdBed = async (bed) => {
    try {
      const r = await axios.post(`${API}/api/pending-admits/${holdItem.id}/hold`, { bed_code: bed.bed_code });
      if (r.data.ok) { setHoldItem(null); fetchPending(); onRefresh(); }
      else alert(r.data.error || 'Hold failed');
    } catch (e) { alert(e.response?.data?.error || 'Hold failed'); }
  };
  const releaseHold = async (pa) => {
    if (!window.confirm('Release the hold on ' + pa.held_bed_code + ' for ' + pa.patient_name + '?')) return;
    try { await axios.post(`${API}/api/pending-admits/${pa.id}/release-hold`); fetchPending(); onRefresh(); }
    catch (e) { alert(e.response?.data?.error || 'Release failed'); }
  };
  const handleRemove = async (item) => {
    try {
      const r = await axios.post(`${API}/api/pending-admits/${item.id}/remove`, { reason: 'Removed from queue' });
      if (r.data && r.data.ok) { fetchPending(); setRemoveConfirm(null); }
      else alert((r.data && r.data.error) || 'Removal failed - please log out and back in, then retry.');
    } catch (e) { alert(e.response?.data?.error || 'Removal failed (' + (e.response?.status || 'network') + ') - please log out and back in, then retry.'); }
  };
  const requestPrescreen = async (pa) => {
    try {
      const r = await axios.post(`${API}/api/leads/${pa.lead_id}/request-prescreen`);
      if (r.data.ok) fetchPending(); else alert(r.data.error || 'Request failed');
    } catch (e) { alert(e.response?.data?.error || 'Request failed'); }
  };
  const createLead = async (pa) => {
    const r = await axios.post(`${API}/api/pending-admits/${pa.id}/create-lead`);
    if (r.data.ok) { alert('Lead created'); fetchPending(); }
  };
  const kipuPush = async (pa) => {
    if (!window.confirm('Push ' + pa.patient_name + ' to Kipu?\n\nThis sends their face sheet (demographics, insurance, expected admit) to the Kipu Pre-Admission queue. Staff still Accepts it in Kipu before a chart is created.')) return;
    try {
      const r = await axios.post(`${API}/api/kipu/push/${pa.id}`, {});
      if (r.data.ok) { alert('Delivered to Kipu ' + DASH + ' check the Pre-Admission queue.'); fetchPending(); }
      else alert(r.data.error || 'Push failed');
    } catch (e) { alert(e.response?.data?.error || 'Push failed'); }
  };

  // Sub-filter derivations — real columns only (ratified rulings)
  const dU = (pa) => daysUntil(pa.expected_admit_date);
  const isToday = (pa) => dU(pa) === 0;
  const isNext48 = (pa) => { const d = dU(pa); return d !== null && d >= 0 && d <= 2; };
  const needsBed = (pa) => !pa.held_bed_code;
  const needsAttention = (pa) => { const d = dU(pa); return d !== null && d < 0; }; // expected date passed, still pending (Chad ruling)
  const counts = {
    today: pendingAdmits.filter(isToday).length,
    next48: pendingAdmits.filter(isNext48).length,
    needsBed: pendingAdmits.filter(needsBed).length,
    prescreen: pendingAdmits.filter(pa => pa.prescreen_done).length,
    attention: pendingAdmits.filter(needsAttention).length,
  };
  const subFiltered = pendingAdmits.filter(pa => {
    if (subFilter === 'today') return isToday(pa);
    if (subFilter === 'next48') return isNext48(pa);
    if (subFilter === 'needsBed') return needsBed(pa);
    if (subFilter === 'prescreen') return pa.prescreen_done;
    if (subFilter === 'attention') return needsAttention(pa);
    return true;
  });
  const shownPending = paSearch
    ? subFiltered.filter(pa => wordsMatch((pa.patient_name || '') + ' ' + (pa.loc || '') + ' ' + (pa.payer || pa.primary_payer || '') + ' ' + (pa.coordinator || ''), paSearch))
    : subFiltered;
  const PAGE = 10;
  const visible = showAll ? shownPending : shownPending.slice(0, PAGE);

  const openingSoon = beds.filter(b => {
    const d = daysUntil(b.expected_dc);
    return b.patient_name && d !== null && d >= 0 && d <= 14;
  });

  const chip = (key, label, n, warn) => (
    <button key={key} onClick={() => { setSubFilter(subFilter === key ? 'all' : key); setShowAll(false); }}
      style={{ padding: '5px 11px', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: subFilter === key ? 700 : 600, fontFamily: 'inherit',
        background: subFilter === key ? tk.aiSoft : tk.surface, color: warn && n > 0 ? tk.warning : (subFilter === key ? tk.ai : tk.textSecondary),
        border: `1px solid ${subFilter === key ? tk.ai : tk.borderSoft}` }}>
      {label} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</b>
    </button>
  );

  const readiness = (pa) => pa.prescreen_done
    ? <span style={{ fontSize: 11, fontWeight: 700, color: tk.success }}>{CHECK} Prescreen</span>
    : pa.prescreen_requested_at
      ? <span style={{ fontSize: 11, fontWeight: 700, color: tk.warning }}>Requested</span>
      : <span style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted }}>Not started</span>;

  const expectedCell = (pa) => {
    if (!pa.expected_admit_date) return <span style={{ color: tk.textMuted }}>{DASH}</span>;
    const late = needsAttention(pa);
    return <span style={{ color: late ? tk.danger : tk.ink, fontWeight: late ? 700 : 600 }}>{formatDate(pa.expected_admit_date)}{late ? ' ' + DOT + ' past' : ''}</span>;
  };

  // Full action set — identical capability to the legacy card buttons
  const actionSet = (pa) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <button onClick={e => { e.stopPropagation(); setAssignItem(pa); }} style={{ ...paBtn, background: tk.ai, color: '#fff', border: 'none', fontWeight: 700 }}>Assign Bed</button>
      <button onClick={e => { e.stopPropagation(); setEditItem(pa); }} style={paBtn}>{PENCIL} Edit</button>
      <button onClick={e => { e.stopPropagation(); setMoveItem(pa); }} title="Move to another facility's bed board" style={paBtn}>{SWAP} Move Board</button>
      {pa.held_bed_code
        ? <button onClick={e => { e.stopPropagation(); releaseHold(pa); }} title={'Holding bed ' + pa.held_bed_code + ' (by ' + (pa.held_by || '?') + ')'} style={{ ...paBtn, color: tk.warning, border: `1px solid ${tk.warning}` }}>Release Hold {DOT} {pa.held_bed_code}</button>
        : <button onClick={e => { e.stopPropagation(); setHoldItem(pa); }} title="Hold a specific bed for this client (travel booked)" style={paBtn}>Hold Bed</button>}
      {pa.lead_id && !pa.prescreen_done && !pa.prescreen_requested_at && (
        <button onClick={e => { e.stopPropagation(); requestPrescreen(pa); }} title="Request a pre-assessment" style={paBtn}>Request Prescreen</button>
      )}
      {!pa.lead_id && (
        <button onClick={e => { e.stopPropagation(); createLead(pa); }} title="Create a lead record" style={paBtn}>+ Lead</button>
      )}
      {emrLive ? null : pa.kipu_pushed_at ? (
        <span title={'Pushed to Kipu ' + formatDate(pa.kipu_pushed_at)} style={{ ...paBtn, color: tk.success, border: `1px solid ${tk.success}`, cursor: 'default' }}>{CHECK} Kipu</span>
      ) : (
        <button onClick={e => { e.stopPropagation(); kipuPush(pa); }} title="Send face sheet to Kipu Pre-Admission" style={paBtn}>{ARROW} Kipu</button>
      )}
      <button onClick={e => { e.stopPropagation(); setRemoveConfirm(pa); }} style={paBtnDanger}>{TIMES} Remove</button>
    </div>
  );

  const expansion = (pa) => (
    <div style={{ background: tk.surfaceSoft, borderLeft: `3px solid ${tk.ai}`, padding: '14px 18px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1.4fr', gap: 16, marginBottom: 12 }}>
        <div>
          <div style={styles.secTitle}>Patient</div>
          <div style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.7 }}>
            <div><b>{pa.patient_name}</b> {pa.gender ? DOT + ' ' + pa.gender : ''}</div>
            {pa.dob && <div>DOB {formatDate(pa.dob)}</div>}
            <div>{pa.payer || 'No payer'}{pa.member_id ? ' ' + DOT + ' ' + pa.member_id : ''}</div>
          </div>
        </div>
        <div>
          <div style={styles.secTitle}>Admission</div>
          <div style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.7 }}>
            <div>LOC: <b>{pa.loc || DASH}</b></div>
            <div>Expected: {expectedCell(pa)}</div>
            {pa.auth_number && <div>Auth # {pa.auth_number}{pa.auth_thru ? ' thru ' + formatDate(pa.auth_thru) : ''}</div>}
            {pa.held_bed_code && <div style={{ color: tk.warning, fontWeight: 700 }}>Holding {pa.held_bed_code}</div>}
          </div>
        </div>
        <div>
          <div style={styles.secTitle}>Placement Info</div>
          <div style={{ fontSize: 12.5, color: tk.ink, lineHeight: 1.7 }}>
            <div>Coordinator: {pa.coordinator || DASH}</div>
            <div>Source: {pa.referral_source || DASH}</div>
            {pa.notes && <div style={{ color: tk.textSecondary, fontStyle: 'italic' }}>{pa.notes}</div>}
          </div>
        </div>
      </div>
      <div style={styles.secTitle}>Actions</div>
      {actionSet(pa)}
    </div>
  );

  return (
    <>
      <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, marginBottom: 16, overflow: 'hidden' }}>
        <div onClick={() => setCollapsed(c => !c)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', cursor: 'pointer', borderBottom: collapsed ? 'none' : `1px solid ${tk.borderSoft}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <UserPlus {...IC} color={tk.blue600} />
            <span style={{ fontSize: 15, fontWeight: 700, color: tk.ink }}>Pending Admits {DASH} {pendingAdmits.length}</span>
            {collapsed && (
              <span style={{ fontSize: 12, color: tk.textSecondary, fontWeight: 600 }}>
                Today {counts.today} {DOT} Needs Bed {counts.needsBed}{counts.attention > 0 ? <span style={{ color: tk.warning }}> {DOT} Needs Attention {counts.attention}</span> : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={e => { e.stopPropagation(); setShowAdd(true); }} style={{ padding: '6px 14px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>+ Add Pending</button>
            {collapsed ? <ChevronRight {...IC} color={tk.textMuted} /> : <ChevronDown {...IC} color={tk.textMuted} />}
          </div>
        </div>
        {!collapsed && (
          <div style={{ padding: '12px 18px 16px' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {chip('today', 'Arriving Today', counts.today)}
              {chip('next48', 'Next 48h', counts.next48)}
              {chip('needsBed', 'Needs Bed', counts.needsBed)}
              {chip('prescreen', 'Prescreen Complete', counts.prescreen)}
              {chip('attention', 'Needs Attention', counts.attention, true)}
            </div>
            {pendingAdmits.length > 0 && (
              <input placeholder={'Search pending ' + DASH + ' name, LOC, payer, or coordinator' + ELLIP} value={paSearch} onChange={e => setPaSearch(e.target.value)}
                style={{ ...styles.input, marginBottom: 10, fontSize: 13 }} />
            )}
            {pendingAdmits.length === 0 ? (
              <div style={{ fontSize: 13, color: tk.textMuted, padding: '8px 0' }}>No pending admits</div>
            ) : shownPending.length === 0 ? (
              <div style={{ fontSize: 13, color: tk.textMuted, padding: '8px 0' }}>No pending admits match{paSearch ? ` "${paSearch}"` : ' this filter'}</div>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visible.map(pa => (
                  <div key={pa.id} style={{ border: `1px solid ${needsAttention(pa) ? tk.danger : tk.borderSoft}`, borderRadius: tk.radiusControl, overflow: 'hidden' }}>
                    <div onClick={() => setExpandedId(expandedId === pa.id ? null : pa.id)} style={{ padding: '10px 12px', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <LocChip loc={pa.loc || '?'} />
                          <b style={{ fontSize: 13.5, color: tk.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pa.patient_name}</b>
                        </div>
                        <button onClick={e => { e.stopPropagation(); setAssignItem(pa); }} style={{ padding: '5px 11px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Assign Bed</button>
                      </div>
                      <div style={{ fontSize: 11.5, color: tk.textSecondary, marginTop: 4 }}>{pa.payer || DASH} {DOT} {readiness(pa)} {DOT} {expectedCell(pa)}</div>
                    </div>
                    {expandedId === pa.id && expansion(pa)}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.6fr 1.1fr 1fr 1fr 0.8fr 0.9fr 1.2fr 0.9fr', gap: 8, padding: '8px 14px', background: tk.surfaceSoft, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  <span>Patient</span><span>LOC</span><span>Payer</span><span>Coordinator</span><span>Source</span><span>Expected</span><span>Readiness</span><span>Notes</span><span></span>
                </div>
                {visible.map(pa => (
                  <div key={pa.id}>
                    <div onClick={() => setExpandedId(expandedId === pa.id ? null : pa.id)}
                      style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.6fr 1.1fr 1fr 1fr 0.8fr 0.9fr 1.2fr 0.9fr', gap: 8, alignItems: 'center', padding: '10px 14px', borderTop: `1px solid ${tk.borderSoft}`, cursor: 'pointer', fontSize: 12.5, color: tk.ink, background: expandedId === pa.id ? tk.aiSoft : tk.surface, borderLeft: needsAttention(pa) ? `3px solid ${tk.danger}` : '3px solid transparent', transition: 'background 160ms' }}>
                      <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pa.patient_name}</span>
                        {pa.gender && <span style={{ fontSize: 10.5, fontWeight: 700, color: pa.gender === 'M' ? tk.genderM : tk.genderF }}>{pa.gender}</span>}
                        {pa.held_bed_code && <span style={{ fontSize: 10, fontWeight: 700, color: tk.warning }}>HELD</span>}
                      </span>
                      <span>{pa.loc ? <LocChip loc={pa.loc} /> : <span style={{ color: tk.textMuted }}>{DASH}</span>}</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: tk.textSecondary }}>{pa.payer || DASH}</span>
                      <span style={{ color: tk.textSecondary }}>{pa.coordinator || DASH}</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: tk.textSecondary }}>{pa.referral_source || DASH}</span>
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{expectedCell(pa)}</span>
                      <span>{readiness(pa)}</span>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: tk.textMuted, fontStyle: pa.notes ? 'italic' : 'normal' }}>{pa.notes || DASH}</span>
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button onClick={e => { e.stopPropagation(); setAssignItem(pa); }} style={{ padding: '5px 11px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Assign Bed</button>
                        <MoreHorizontal {...ICS} color={tk.textMuted} />
                      </span>
                    </div>
                    {expandedId === pa.id && expansion(pa)}
                  </div>
                ))}
              </div>
            )}
            {shownPending.length > PAGE && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
                <button onClick={() => setShowAll(s => !s)} style={{ ...paBtn, fontWeight: 700 }}>
                  {showAll ? 'Show first ' + PAGE : `View All ${shownPending.length}`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Opening Soon — expected DC within 14 days (existing derivation, restyled) */}
      {openingSoon.length > 0 && (
        <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, padding: '14px 18px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CalendarClock {...IC} color={tk.success} />
            <span style={{ fontSize: 13, fontWeight: 700, color: tk.ink }}>Opening Soon {DASH} next 14 days</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted }}>expected, not guaranteed</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {openingSoon.map(b => {
              const d = daysUntil(b.expected_dc);
              return (
                <div key={b.bed_code} style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: '9px 13px', minWidth: 170 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: tk.ink }}>{b.patient_name} {DASH} {b.bed_code}</div>
                  <div style={{ fontSize: 11, color: tk.textSecondary }}>{b.episode_loc || b.loc} bed frees up {formatDate(b.expected_dc)}</div>
                  <div style={{ fontSize: 11, color: tk.success, fontWeight: 700, marginTop: 2 }}>{d === 0 ? 'Today' : `${d} day${d !== 1 ? 's' : ''}`}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Hold a bed (verbatim flow, tokens) */}
      {holdItem && (
        <div style={styles.overlay}>
          <div style={{ background: tk.surface, borderRadius: tk.radiusCard, padding: '20px 24px', width: 'min(400px, 92vw)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: tk.ink, marginBottom: 4 }}>Hold a bed for {holdItem.patient_name}</div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginBottom: 14 }}>The bed shows as reserved but stays out of the census until they arrive. Any staff can mark them arrived.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
              {beds.filter(b => !b.patient_name && b.bed_type !== 'staff').map(b => (
                <button key={b.bed_code} onClick={() => holdBed(b)}
                  style={{ padding: '8px 13px', background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, fontSize: 12.5, fontWeight: 600, color: tk.ink, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  {(b.house_name ? b.house_name + ' ' + DOT + ' ' : '') + (b.bed_label || b.bed_code)}{b.loc ? ' ' + DOT + ' ' + b.loc : ''}</button>
              ))}
              {beds.filter(b => !b.patient_name && b.bed_type !== 'staff').length === 0 && <div style={{ fontSize: 12, color: tk.textMuted }}>No empty beds on this board.</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setHoldItem(null)} style={{ padding: '7px 16px', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 12.5, cursor: 'pointer', color: tk.textSecondary, fontFamily: 'inherit' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Move to another facility (verbatim flow, tokens) */}
      {moveItem && (
        <div style={styles.overlay}>
          <div style={{ background: tk.surface, borderRadius: tk.radiusCard, padding: '20px 24px', width: 'min(380px, 92vw)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: tk.ink, marginBottom: 4 }}>Move {moveItem.patient_name}</div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginBottom: 14 }}>Pick the bed board this client should be on. They leave this board and appear there.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 300, overflowY: 'auto' }}>
              {moveOrgs.filter(o => String(o.id) !== String(facilityId)).map(o => (
                <button key={o.id} onClick={() => moveTo(o)}
                  style={{ padding: '9px 13px', background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, fontSize: 13, fontWeight: 600, color: tk.ink, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                  {o.display_name || o.org_name}</button>
              ))}
              {moveOrgs.length === 0 && <div style={{ fontSize: 12, color: tk.textMuted }}>Loading facilities{ELLIP}</div>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setMoveItem(null)} style={{ padding: '7px 16px', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, fontSize: 12.5, cursor: 'pointer', color: tk.textSecondary, fontFamily: 'inherit' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showAdd && <PendingAdmitModal facilityId={facilityId} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); fetchPending(); }} />}
      {editItem && <PendingAdmitModal facilityId={facilityId} editData={editItem} onClose={() => setEditItem(null)} onSuccess={() => { setEditItem(null); fetchPending(); }} />}
      {assignItem && <AssignBedModal pending={assignItem} beds={beds} facilityId={facilityId} onClose={() => setAssignItem(null)} onSuccess={() => { setAssignItem(null); fetchPending(); onRefresh(); }} />}
      {removeConfirm && (
        <Modal title="Remove from Queue" onClose={() => setRemoveConfirm(null)}>
          <p style={{ fontSize: 14, color: tk.ink, marginBottom: 16 }}>
            Remove <strong>{removeConfirm.patient_name}</strong> from the pending admits queue?
            {removeConfirm.lead_id && ' Their lead will be tagged for follow-up.'}
          </p>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => setRemoveConfirm(null)}>Cancel</button>
            <button style={styles.btnRed} onClick={() => handleRemove(removeConfirm)}>Remove & Tag Follow Up</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── ADMIT MODAL (fields + endpoint verbatim; tokens) ───
function AdmitModal({ bed, facilityId, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({ patient_name: '', gender: '', dob: '', admit_date: today, primary_payer: '', loc: bed.loc || 'DTX', member_id: '', therapist: '', case_manager: '', coordinator: '', mkt_source: '', policy_id: '', rx_bin: '', group_num: '', rx_pcn: '', auth_num: '', auth_thru: '', expected_dc: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.patient_name.trim()) { setError('Patient name is required.'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/admit`, { ...form, bed_id: bed.bed_code, facility_id: facilityId });
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error admitting patient.'); }
    setSaving(false);
  };
  return (
    <Modal title={`Admit Patient ${DASH} ${bed.bed_label} (${bed.house_name})`} onClose={onClose}>
      {error && <div style={styles.errBox}>{error}</div>}
      <div style={styles.grid2}>
        <Field label="Patient Name *"><input style={styles.input} value={form.patient_name} onChange={e => set('patient_name', e.target.value)} placeholder="First Last" /></Field>
        <Field label="Gender"><select style={styles.input} value={form.gender} onChange={e => set('gender', e.target.value)}><option value="">{DASH}</option><option value="M">Male</option><option value="F">Female</option></select></Field>
        <Field label="Date of Admission *"><input style={styles.input} type="date" value={form.admit_date} onChange={e => set('admit_date', e.target.value)} /></Field>
        <Field label="Date of Birth"><span style={{ display:'flex', alignItems:'center' }}><input style={styles.input} type="date" value={form.dob} onChange={e => set('dob', e.target.value)} /><AgeChip dob={form.dob} /></span></Field>
        <Field label="Payer *"><PayerSelect inputStyle={styles.input} value={form.primary_payer} onChange={v => set('primary_payer', v)} /></Field>
        <Field label="Level of Care *"><select style={styles.input} value={form.loc} onChange={e => set('loc', e.target.value)}>{LOCS.map(l => <option key={l}>{l}</option>)}</select></Field>
        <Field label="Member ID"><input style={styles.input} value={form.member_id} onChange={e => set('member_id', e.target.value)} /></Field>
        <Field label="Auth Approved Through"><input style={styles.input} type="date" value={form.auth_thru} onChange={e => set('auth_thru', e.target.value)} /></Field>
        <Field label="Auth Number"><input style={styles.input} value={form.auth_num} onChange={e => set('auth_num', e.target.value)} /></Field>
        <Field label="Expected Discharge"><input style={styles.input} type="date" value={form.expected_dc} onChange={e => set('expected_dc', e.target.value)} /></Field>
        <Field label="Therapist"><StaffSelect kind="therapist" facilityId={facilityId} inputStyle={styles.input} value={form.therapist} onChange={v => set('therapist', v)} /></Field>
        <Field label="Case Manager"><StaffSelect kind="case_manager" facilityId={facilityId} inputStyle={styles.input} value={form.case_manager} onChange={v => set('case_manager', v)} /></Field>
        <Field label="Coordinator"><select style={styles.input} value={form.coordinator} onChange={e => set('coordinator', e.target.value)}>{COORDINATORS.map(c => <option key={c} value={c}>{c || DASH}</option>)}</select></Field>
        <Field label="Marketing Source"><select style={styles.input} value={form.mkt_source} onChange={e => set('mkt_source', e.target.value)}>{SOURCES.map(s => <option key={s} value={s}>{s || DASH}</option>)}</select></Field>
      </div>
      <div style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: 12, marginBottom: 12 }}>
        <div style={styles.secTitle}>Insurance Details</div>
        <div style={styles.grid2}>

          <Field label="RX BIN"><input style={styles.input} value={form.rx_bin} onChange={e => set('rx_bin', e.target.value)} /></Field>
          <Field label="Group Number"><input style={styles.input} value={form.group_num} onChange={e => set('group_num', e.target.value)} /></Field>
          <Field label="RX PCN"><input style={styles.input} value={form.rx_pcn} onChange={e => set('rx_pcn', e.target.value)} /></Field>
        </div>
      </div>
      <Field label="Notes"><textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
      <div style={styles.btnRow}>
        <button style={styles.btnGhost} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={submit} disabled={saving}>{saving ? 'Admitting' + ELLIP : 'Admit Patient'}</button>
      </div>
    </Modal>
  );
}

// Display-only role read from the JWT (server enforces the real gate).
function tokenRole() {
  try {
    const t = localStorage.getItem('tcio_token') || '';
    return (JSON.parse(atob(t.split('.')[1])) || {}).role || '';
  } catch (e) { return ''; }
}

// ─── PATIENT DETAIL MODAL (all 5 modes verbatim; tokens; 42 CFR block intact) ───
function PatientModal({ bed, facilityId, allBeds, onClose, onSuccess }) {
  const loc = bed.episode_loc || bed.loc;
  const lc = locC(loc);
  const daysIn = daysSince(bed.admit_date);
  const authDaysLeft = daysUntil(bed.auth_thru);
  const authExpired = authDaysLeft !== null && authDaysLeft < 0;
  const authExpiring = !authExpired && authDaysLeft !== null && authDaysLeft <= 3;
  const [mode, setMode] = useState('view');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState({ patient_name: bed.patient_name || '', gender: bed.patient_gender || '', dob: toInputDate(bed.dob), admit_date: toInputDate(bed.admit_date), member_id: bed.member_id || bed.policy_id || '', primary_payer: bed.primary_payer || '', loc: bed.episode_loc || '', therapist: bed.therapist || '', case_manager: bed.case_manager || '', coordinator: bed.coordinator || '', mkt_source: bed.mkt_source || '', policy_id: bed.policy_id || '', rx_bin: bed.rx_bin || '', group_num: bed.group_num || '', rx_pcn: bed.rx_pcn || '', auth_num: bed.auth_num || '', auth_thru: toInputDate(bed.auth_thru), expected_dc: toInputDate(bed.expected_dc), notes: bed.notes || '' });
  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v }));
  const [dcReason, setDcReason] = useState('Completed');
  const [naReason, setNaReason] = useState('');
  const isExec = ['ceo','admin'].includes(tokenRole());
  const [dcDate, setDcDate] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  });
  const emptyBeds = allBeds.filter(b => !b.patient_name && b.bed_code !== bed.bed_code);
  const [moveBed, setMoveBed] = useState(emptyBeds[0]?.bed_code || '');
  const [moveLoc, setMoveLoc] = useState('');
  const [tFacilities, setTFacilities] = useState([]);
  const [tFacility, setTFacility] = useState('');
  const [tLoc, setTLoc] = useState('');
  const [tBeds, setTBeds] = useState([]);
  const [tBed, setTBed] = useState('');
  const [tReason, setTReason] = useState('');
  const [resv, setResv] = useState(null);
  useEffect(() => {
    let live = true;
    if (!bed || !bed.episode_id) return undefined;
    axios.get(`${API}/api/transfer/reservation/${bed.episode_id}`)
      .then(r => { if (live) setResv((r.data && r.data.data) || null); })
      .catch(() => {});
    return () => { live = false; };
  }, [bed]);
  const cancelReserve = async () => {
    if (!resv) return;
    if (!window.confirm('Cancel the reservation for ' + bed.patient_name + ' at ' + resv.facility_name + ' (' + (resv.held_bed_code || 'no bed') + ')?\n\nThe bed is released on that board.')) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/transfer/reserve/cancel`, { episode_id: bed.episode_id });
      setResv(null);
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error cancelling the reservation.'); }
    setSaving(false);
  };
  const [tDate, setTDate] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  });
  const openTransfer = async () => {
    setMode('transfer'); setError('');
    try {
      let list = [];
      try { const d = await axios.get(`${API}/api/transfer/destinations/${bed.episode_id}`); list = d.data.data || []; } catch (e0) { list = []; }
      if (!list.length) { const r = await axios.get(`${API}/api/facilities`); list = r.data.data || []; }
      setTFacilities(list);
    } catch (e) { setError('Could not load facilities.'); }
  };
  const openReserve = async () => { await openTransfer(); setMode('reserve'); };
  const tSel = tFacilities.find(x => String(x.id) === String(tFacility));
  const tSendOnly = false && !!tSel; // the sender now picks the bed (open-beds view); request-without-a-bed is retired
  const pickTFacility = async (fid) => {
    setTFacility(fid); setTBed(''); setTBeds([]);
    if (!fid) return;
    try { const r = await axios.get(`${API}/api/transfer/open-beds?episode_id=${bed.episode_id}&facility_id=${fid}`);
      const list = (r.data.data || []);
      setTBeds(list);
    } catch (e) { setError('Could not load beds for that facility.'); }
  };
  const saveEdit = async () => { setSaving(true); setError(''); try { { const { policy_id: dropPolicy, ...body } = editForm; void dropPolicy; await axios.patch(`${API}/api/episode/${bed.episode_id}/details`, body); } onSuccess(); } catch (e) { setError(e.response?.data?.error || 'Error saving.'); } setSaving(false); };
  const saveDischarge = async () => { if (!dcReason) { setError('Select a discharge reason.'); return; }
    if (!dcDate) { setError('Enter the discharge date.'); return; }
    const [dy, dm, dd] = dcDate.split('-');
    const pretty = `${dm}/${dd}/${dy}`;
    if (!window.confirm(`Discharge ${bed.patient_name} with discharge date ${pretty}?\n\nThis date drives billing and UR coverage ${DASH} confirm it is correct.`)) return;
    setSaving(true); setError(''); try { await axios.post(`${API}/api/discharge`, { episode_id: bed.episode_id, discharge_date: dcDate, discharge_reason: dcReason, discharge_type: dcReason === 'Completed' ? 'planned' : 'unplanned' }, { headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}` } }); onSuccess(); } catch (e) { setError(e.response?.data?.error || 'Error discharging.'); } setSaving(false); };
  const saveNeverAdmitted = async () => {
    if (naReason.trim().length < 10) { setError('A reason of at least 10 characters is required.'); return; }
    if (!window.confirm(`Void ${bed.patient_name}'s admission as NEVER OCCURRED?\n\nThe bed is released and the card returns to the pending board. This is blocked if any clinical documentation exists. This is for admits that never physically happened ${DASH} if the patient received any care, use Discharge or Transfer instead.`)) return;
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/api/episodes/${bed.episode_id}/never-admitted`, { reason: naReason.trim() });
      window.alert(r.data.message);
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error voiding admission.'); }
    setSaving(false);
  };
  const saveMove = async () => { if (!moveBed) { setError('Select a destination bed.'); return; } setSaving(true); setError(''); try { await axios.post(`${API}/api/move`, { episode_id: bed.episode_id, new_bed_id: moveBed, new_loc: moveLoc || null }); onSuccess(); } catch (e) { setError(e.response?.data?.error || 'Error moving patient.'); } setSaving(false); };
  const saveTransfer = async () => {
    if (!tFacility || !tLoc || !tBed) { setError('Facility, LOC, and bed are required.'); return; }
    const fname = (tFacilities.find(x => String(x.id) === String(tFacility)) || {}).org_name || 'target facility';
    if (!window.confirm(`Transfer ${bed.patient_name} to ${fname} (${tLoc}) on ${tDate}?\n\nThis closes the stay here now. If they are going to another facility, the bed there is held and they are admitted when that team clicks Arrived. A NEW authorization is required at the target.`)) return;
    setSaving(true); setError('');
    try {
      const cross = String(tFacility) !== String(bed.episode_facility_id || '');
      const r = await axios.post(`${API}/api/transfer${cross ? '/depart' : ''}`, {
        episode_id: bed.episode_id, target_facility_id: tFacility, target_loc: tLoc,
        target_bed_id: tBed, transfer_date: tDate, reason: tReason || null });
      window.alert(cross ? r.data.message : `${r.data.message}\nMeds continued: ${r.data.meds_continued} ${DOT} discontinued: ${r.data.meds_discontinued}`);
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error transferring patient.'); }
    setSaving(false);
  };
  const saveSend = async () => {
    if (!tFacility || !tLoc) { setError('Facility and level of care are required.'); return; }
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/api/transfer/send`, {
        episode_id: bed.episode_id, target_facility_id: tFacility, target_loc: tLoc,
        expected_date: tDate, reason: tReason || null });
      window.alert(r.data.message);
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error sending the transfer request.'); }
    setSaving(false);
  };
  const saveReserve = async () => {
    if (!tFacility || !tLoc || !tBed) { setError('Facility, LOC, and bed are required.'); return; }
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/api/transfer/reserve`, {
        episode_id: bed.episode_id, target_facility_id: tFacility, target_loc: tLoc,
        target_bed_id: tBed, expected_date: tDate });
      window.alert(r.data.message + "\n\nThe bed now shows as held on the destination board. When the client moves, use Transfer here and the reservation closes on its own.");
      onSuccess();
    } catch (e) { setError(e.response?.data?.error || 'Error reserving the bed.'); }
    setSaving(false);
  };
  const saveNote = async () => { setSaving(true); setError(''); try { await axios.patch(`${API}/api/episode/${bed.episode_id}/details`, { notes: editForm.notes }); onSuccess(); } catch (e) { setError('Error saving note.'); } setSaving(false); };
  const saveAuth = async () => { setSaving(true); setError(''); try { await axios.patch(`${API}/api/episode/${bed.episode_id}/details`, { auth_thru: editForm.auth_thru || null, auth_num: editForm.auth_num || null }); onSuccess(); } catch (e) { setError('Error saving auth.'); } setSaving(false); };

  return (
    <Modal title={mode === 'view' ? bed.patient_name : mode === 'edit' ? `Edit ${DASH} ${bed.patient_name}` : mode === 'discharge' ? `Discharge ${DASH} ${bed.patient_name}` : mode === 'transfer' ? `Transfer ${DASH} ${bed.patient_name}` : mode === 'reserve' ? `Reserve a bed ${DASH} ${bed.patient_name}` : `Move ${DASH} ${bed.patient_name}`} onClose={onClose}>
      {error && <div style={styles.errBox}>{error}</div>}
      {mode === 'view' && (
        <>
          <div style={{ ...styles.summaryBox, borderLeft: `4px solid ${lc.c}` }}>
            <div style={styles.summaryRow}><span>Bed</span><b>{bed.bed_code} {DOT} {bed.house_name}</b></div>
            <div style={styles.summaryRow}><span>Day</span><b>{daysIn !== null ? `Day ${daysIn}` : DASH}</b></div>
            <div style={styles.summaryRow}><span>Admitted</span><b>{formatDate(bed.admit_date) || DASH}</b></div>
            <div style={styles.summaryRow}><span>Gender</span><b>{bed.patient_gender || DASH}</b></div>
            <div style={styles.summaryRow}><span>Payer</span><b>{bed.primary_payer || DASH}</b></div>
            <div style={styles.summaryRow}><span>LOC</span><b>{loc || DASH}</b></div>
            <div style={styles.summaryRow}><span>Therapist</span><b>{bed.therapist || DASH}</b></div>
            <div style={styles.summaryRow}><span>Case Manager</span><b>{bed.case_manager || DASH}</b></div>
            <div style={styles.summaryRow}><span>Coordinator</span><b>{bed.coordinator || DASH}</b></div>
            <div style={styles.summaryRow}><span>Auth Number</span><b style={{ fontFamily: 'monospace', fontSize: 13 }}>{bed.auth_num || DASH}</b></div>
            <div style={styles.summaryRow}><span>Auth Approved Thru</span><b style={{ color: authExpired ? tk.danger : authExpiring ? tk.warning : 'inherit' }}>{formatDate(bed.auth_thru) || DASH}{authExpired && ' ' + DOT + ' EXPIRED'}{authExpiring && ` ${DOT} ${authDaysLeft}d left`}</b></div>
            <div style={styles.summaryRow}><span>Expected Discharge</span><b>{formatDate(bed.expected_dc) || 'not set'}</b></div>
            <div style={styles.summaryRow}><span>UR Due</span><b style={{ color: bed.ur_status === 'NEW' ? tk.warning : 'inherit' }}>{formatDate(bed.ur_due) || DASH}{bed.ur_status ? ` ${DOT} ${bed.ur_status}` : ''}</b></div>
          </div>
          <div style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: 12, marginBottom: 12 }}>
            <div style={{ ...styles.secTitle, color: tk.teal }}>Phone Releases & Emergency Contact</div>
            {!bed.patient_id ? (
              <div style={{ fontSize: 12, color: tk.textMuted }}>Not linked to an EMR chart yet {DASH} release info unavailable.</div>
            ) : (
              <>
                <div style={styles.summaryRow}><span>Emergency Contact</span><b>{bed.emergency_contact ? `${bed.emergency_contact.name}${bed.emergency_contact.relationship ? ' (' + bed.emergency_contact.relationship + ')' : ''} ${DOT} ${bed.emergency_contact.phone || 'no phone'}` : DASH}</b></div>
                {(bed.may_speak_with || []).length > 0 ? (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: tk.success, marginBottom: 4 }}>MAY SPEAK WITH</div>
                    {(bed.may_speak_with || []).map((rel, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, background: tk.successBg || tk.surfaceSoft, border: `1px solid ${tk.success}`, borderRadius: 6, padding: '5px 10px', marginBottom: 4 }}>
                        <b style={{ color: tk.success }}>{rel.name}{rel.relationship ? ' ' + DASH + ' ' + rel.relationship : ''}</b>
                        <span style={{ color: tk.success }}>{rel.expiry ? 'exp ' + formatDate(rel.expiry) : 'no expiry'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginTop: 8, background: tk.dangerBg, border: `1px solid ${tk.danger}`, borderRadius: 6, padding: '7px 10px', fontSize: 12.5, fontWeight: 700, color: tk.danger }}>
                    No active releases {DASH} cannot confirm or deny this patient is here.
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: 12, marginBottom: 12 }}>
            <div style={styles.secTitle}>Insurance Details</div>
            <div style={styles.summaryRow}><span>Member ID</span><b style={{ fontFamily: 'monospace' }}>{bed.member_id || bed.policy_id || DASH}</b></div>
            {bed.member_id && bed.policy_id && (bed.member_id === bed.policy_id) === false && <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600, margin: '2px 0 6px' }}>A second, different member ID is also on file: {bed.policy_id}. Check the insurance card, then press Edit and save the correct Member ID.</div>}
            <div style={styles.summaryRow}><span>RX BIN</span><b style={{ fontFamily: 'monospace' }}>{bed.rx_bin || DASH}</b></div>
            <div style={styles.summaryRow}><span>Group Number</span><b style={{ fontFamily: 'monospace' }}>{bed.group_num || DASH}</b></div>
            <div style={styles.summaryRow}><span>RX PCN</span><b style={{ fontFamily: 'monospace' }}>{bed.rx_pcn || DASH}</b></div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Field label="Team Note"><textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={editForm.notes} onChange={e => setEdit('notes', e.target.value)} /></Field>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}><button style={{ ...styles.btnGhost, flex: 1 }} onClick={saveNote} disabled={saving}>Save Note</button></div>
            <div style={styles.grid2}>
              <Field label="Auth Approved Thru"><input style={styles.input} type="date" value={editForm.auth_thru} onChange={e => setEdit('auth_thru', e.target.value)} /></Field>
              <Field label="Auth Number"><input style={styles.input} value={editForm.auth_num} onChange={e => setEdit('auth_num', e.target.value)} /></Field>
            </div>
            <button style={{ ...styles.btnGhost, width: '100%', marginBottom: 8 }} onClick={saveAuth} disabled={saving}>Save Auth</button>
          </div>
          <div style={styles.btnRow}>
            <button style={styles.btnPrimary} onClick={() => setMode('edit')}>{PENCIL} Edit Info</button>
            <button style={styles.btnBlue} onClick={() => setMode('move')}>{MOVEG} Move</button>
            <button style={styles.btnBlue} onClick={openTransfer}>{SWAP} Transfer</button>
            {resv
              ? <button style={styles.btnGhost} onClick={cancelReserve} title={'Reserved at ' + resv.facility_name + (resv.expected_date ? ', expected ' + resv.expected_date : '')}>Cancel reservation {DOT} {resv.held_bed_code || resv.facility_name}</button>
              : <button style={styles.btnGhost} onClick={openReserve}>Reserve at another facility</button>}
            <button style={styles.btnRed} onClick={() => setMode('discharge')}>Discharge{ELLIP}</button>
            {isExec && <button style={{ ...styles.btnGhost, fontSize:12, color:'#8291A1' }} onClick={() => setMode('never')}>Never Admitted{ELLIP}</button>}
          </div>
          <div style={{ marginTop: 8 }}><button style={{ ...styles.btnGhost, width: '100%' }} onClick={onClose}>Close</button></div>
        </>
      )}
      {mode === 'edit' && (
        <>
          <div style={styles.grid2}>
            <Field label="Patient Name"><input style={styles.input} value={editForm.patient_name} onChange={e => setEdit('patient_name', e.target.value)} /></Field>
            <Field label="Gender"><select style={styles.input} value={editForm.gender} onChange={e => setEdit('gender', e.target.value)}><option value="">{DASH}</option><option value="M">Male</option><option value="F">Female</option></select></Field>
            <Field label="Date of Birth"><span style={{ display:'flex', alignItems:'center' }}><input style={styles.input} type="date" value={editForm.dob} onChange={e => setEdit('dob', e.target.value)} /><AgeChip dob={editForm.dob} /></span></Field>
            <Field label="Admit Date"><input style={styles.input} type="date" value={editForm.admit_date} onChange={e => setEdit('admit_date', e.target.value)} /></Field>
            <Field label="Member ID"><input style={styles.input} value={editForm.member_id} onChange={e => setEdit('member_id', e.target.value)} /></Field>
            <Field label="Payer"><PayerSelect inputStyle={styles.input} value={editForm.primary_payer} onChange={v => setEdit('primary_payer', v)} /></Field>
            <Field label="Level of Care"><select style={styles.input} value={editForm.loc} onChange={e => setEdit('loc', e.target.value)}>{LOCS.map(l => <option key={l}>{l}</option>)}</select></Field>
            <Field label="Therapist"><StaffSelect kind="therapist" facilityId={facilityId} inputStyle={styles.input} value={editForm.therapist} onChange={v => setEdit('therapist', v)} /></Field>
            <Field label="Case Manager"><StaffSelect kind="case_manager" facilityId={facilityId} inputStyle={styles.input} value={editForm.case_manager} onChange={v => setEdit('case_manager', v)} /></Field>
            <Field label="Coordinator"><select style={styles.input} value={editForm.coordinator} onChange={e => setEdit('coordinator', e.target.value)}>{COORDINATORS.map(c => <option key={c} value={c}>{c || DASH}</option>)}</select></Field>
            <Field label="Marketing Source"><select style={styles.input} value={editForm.mkt_source} onChange={e => setEdit('mkt_source', e.target.value)}>{SOURCES.map(s => <option key={s} value={s}>{s || DASH}</option>)}</select></Field>
          </div>
          <div style={{ background: tk.surfaceSoft, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusControl, padding: 12, marginBottom: 12 }}>
            <div style={styles.secTitle}>Insurance</div>
            <div style={styles.grid2}>

              <Field label="RX BIN"><input style={styles.input} value={editForm.rx_bin} onChange={e => setEdit('rx_bin', e.target.value)} /></Field>
              <Field label="Group Number"><input style={styles.input} value={editForm.group_num} onChange={e => setEdit('group_num', e.target.value)} /></Field>
              <Field label="RX PCN"><input style={styles.input} value={editForm.rx_pcn} onChange={e => setEdit('rx_pcn', e.target.value)} /></Field>
            </div>
          </div>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => setMode('view')}>Back</button>
            <button style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={saveEdit} disabled={saving}>{saving ? 'Saving' + ELLIP : 'Save Changes'}</button>
          </div>
        </>
      )}
      {mode === 'never' && (
        <>
          <div style={{ background:'#FFF7E6', border:'1px solid #F59E0B', borderRadius:8, padding:'10px 12px', fontSize:12.5, marginBottom:12, color:'#22303E' }}>
            <b>Never Admitted</b> voids this admission as never-occurred: the episode is closed as a zero-day record (history preserved), the bed is released, and the card returns to the pending board. It is <b>blocked if any clinical documentation exists</b> {DASH} notes, signatures, MAR, vitals, or rounds. If {bed.patient_name} received any care, use Discharge or Transfer instead.
          </div>
          <Field label="Reason (required) *"><textarea style={{ ...styles.input, height:60, resize:'vertical' }} value={naReason} onChange={e => setNaReason(e.target.value)} placeholder="e.g. Client no-showed; admit clicked in error before arrival was confirmed" /></Field>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => { setMode('view'); setError(''); }}>Back</button>
            <button style={{ ...styles.btnRed, opacity: saving ? 0.6 : 1 }} onClick={saveNeverAdmitted} disabled={saving}>{saving ? 'Voiding' + ELLIP : 'Confirm Never Admitted'}</button>
          </div>
        </>
      )}
      {mode === 'discharge' && (
        <>
          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}><span>Patient</span><b>{bed.patient_name}</b></div>
            <div style={styles.summaryRow}><span>Admitted</span><b>{formatDate(bed.admit_date)}</b></div>
            <div style={styles.summaryRow}><span>Days on Census</span><b>{daysIn !== null ? `${daysIn} days` : DASH}</b></div>
          </div>
          <Field label="Discharge Date"><input style={styles.input} type="date" value={dcDate} onChange={e => setDcDate(e.target.value)} /></Field>
          <Field label="Discharge Reason *"><select style={styles.input} value={dcReason} onChange={e => setDcReason(e.target.value)}>{DISCHARGE_REASONS.map(r => <option key={r}>{r}</option>)}</select></Field>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => setMode('view')}>Back</button>
            <button style={{ ...styles.btnRed, opacity: saving ? 0.6 : 1 }} onClick={saveDischarge} disabled={saving}>{saving ? 'Discharging' + ELLIP : 'Confirm Discharge'}</button>
          </div>
        </>
      )}
      {mode === 'move' && (
        <>
          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}><span>From</span><b>{bed.bed_code} {DOT} {bed.house_name}</b></div>
            <div style={styles.summaryRow}><span>Current LOC</span><b>{loc}</b></div>
          </div>
          <Field label="Destination Bed">
            <select style={styles.input} value={moveBed} onChange={e => setMoveBed(e.target.value)}>
              <option value="">{DASH} Select empty bed {DASH}</option>
              {emptyBeds.map(b => <option key={b.bed_code} value={b.bed_code}>{b.bed_code} ({b.house_name})</option>)}
            </select>
          </Field>
          <Field label="New Level of Care (optional)">
            <select style={styles.input} value={moveLoc} onChange={e => setMoveLoc(e.target.value)}>
              <option value="">Keep {loc}</option>
              {LOCS.filter(l => l !== loc).map(l => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => setMode('view')}>Back</button>
            <button style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={saveMove} disabled={saving}>{saving ? 'Moving' + ELLIP : 'Confirm Move'}</button>
          </div>
        </>
      )}
      {(mode === 'transfer' || mode === 'reserve') && (
        <>
          <div style={styles.summaryBox}>
            <div style={styles.summaryRow}><span>From</span><b>{bed.bed_code} {DOT} {bed.house_name} {DOT} {loc}</b></div>
            <div style={styles.summaryRow}><span>Patient</span><b>{bed.patient_name}</b></div>
          </div>
          <Field label="Target Facility *">
            <select style={styles.input} value={tFacility} onChange={e => pickTFacility(e.target.value)}>
              <option value="">{DASH} Select facility {DASH}</option>
              {tFacilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
            </select>
          </Field>
          <Field label="Target Level of Care *">
            <select style={styles.input} value={tLoc} onChange={e => setTLoc(e.target.value)}>
              <option value="">{DASH} Select LOC {DASH}</option>
              {LOCS.map(l => <option key={l}>{l}</option>)}
            </select>
          </Field>
          {tSendOnly ? <div style={{ fontSize: 12, color: tk.textSecondary, margin: '4px 0 10px' }}>You do not have access to the bed board at that facility. Their team assigns the bed when they accept, and the client stays in this bed until then.</div> : <Field label="Target Bed *">
            <select style={styles.input} value={tBed} onChange={e => setTBed(e.target.value)}>
              <option value="">{tFacility ? (tBeds.length ? DASH + ' Select empty bed ' + DASH : 'No empty beds at facility') : 'Select facility first'}</option>
              {tBeds.map(b => <option key={b.bed_code} value={b.bed_code}>{b.bed_code} ({b.house_name})</option>)}
            </select>
          </Field>}
          <Field label={mode === 'reserve' ? 'Expected Move Date *' : 'Transfer Date *'}><input style={styles.input} type="date" value={tDate} onChange={e => setTDate(e.target.value)} /></Field>
          {mode === 'transfer' && <Field label="Reason (optional)"><input style={styles.input} value={tReason} placeholder={'Step-down / step-up / better fit' + ELLIP} onChange={e => setTReason(e.target.value)} /></Field>}
          <div style={{ fontSize: 12, color: tk.textSecondary, margin: '4px 0 10px' }}>
            {mode === 'reserve' ? 'This holds the bed on the destination board. Nothing changes for the client until you run Transfer.' : 'Carries: identity, payer, ROIs, continue-on-discharge meds. Fresh at target: intake packet, authorization. Notes stay with the current episode.'}
          </div>
          <div style={styles.btnRow}>
            <button style={styles.btnGhost} onClick={() => setMode('view')}>Back</button>
            <button style={{ ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={tSendOnly ? saveSend : (mode === 'reserve' ? saveReserve : saveTransfer)} disabled={saving}>{saving ? (mode === 'reserve' ? 'Reserving' : 'Transferring') + ELLIP : (tSendOnly ? 'Send Transfer Request' : mode === 'reserve' ? 'Reserve Bed' : 'Confirm Transfer')}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ─── BED CARD (all behaviors verbatim: rearrange overlay, census reassign, holds, arrived) ───
// STAFF BED (2026-09-30, Chad ruling): a bed employees live in. Stays on the board, never counted,
// never assignable. CEO / admin / program manager can return it to client use.
function StaffBedTile({ bed, onRefresh }) {
  const canFlip = ['ceo', 'admin', 'program_manager'].includes(localStorage.getItem('tcio_role'));
  const release = async () => {
    if (!canFlip) return;
    if (!window.confirm('Return ' + bed.bed_code + ' to client use?\n\nIt becomes a regular open bed and counts toward open beds again.')) return;
    try { await axios.patch(API + '/api/beds/' + bed.id + '/staff', { staff: false }); onRefresh(); }
    catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };
  return (
    <div onClick={release} title={(bed.set_aside_note || 'Staff bed') + (canFlip ? ' - click to return it to client use' : ' - not available for clients')}
      style={{ background: tk.surfaceSoft, border: `1px dashed ${tk.border}`, borderRadius: tk.radiusCard, padding: '10px 12px', minHeight: 86, cursor: canFlip ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted }}>{bed.bed_label || bed.bed_code}</div>
      <div style={{ fontSize: bed.set_aside_note ? 12 : 13, fontWeight: 600, color: tk.textSecondary, marginTop: 8, lineHeight: 1.25 }}>{bed.set_aside_note || 'Staff'}</div>
      <div style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 2 }}>{bed.set_aside_note ? 'not available' : 'not counted'}</div>
    </div>
  );
}

function BedCard({ bed, allBeds, facilityId, onRefresh, risk, roundOverdue, facilities, hold, rearrange, plannedOcc, selectedEp, patientsByEp, tapBed, canSeeDollars }) {
  const holdHours = hold && hold.held_at ? Math.floor((Date.now() - new Date(hold.held_at)) / 3600000) : 0;
  const holdOverdue = holdHours >= 24;
  const [modal, setModal] = useState(null);
  // Held-bed arrival goes through the real Assign Bed modal (LOC to attempt, admit date,
  // therapist/case manager) instead of a bare confirm - ticket 06999bb9.
  const markArrived = (e) => { e.stopPropagation(); setModal('arrive'); };
  const canFlipStaff = ['ceo', 'admin', 'program_manager'].includes(localStorage.getItem('tcio_role')) && bed.bed_type !== 'program' && bed.house_type !== 'program';
  const markStaff = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Set ' + bed.bed_code + ' aside for staff?\n\nIt stays on the board as a Staff bed, stops counting as an open bed, and cannot be given to a client until it is returned to client use.')) return;
    try { await axios.patch(API + '/api/beds/' + bed.id + '/staff', { staff: true }); onRefresh(); }
    catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };
  const [reassignOpen, setReassignOpen] = useState(false);
  useEffect(() => {
    if (!reassignOpen) return;
    const close = () => setReassignOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [reassignOpen]);
  const assignCensus = async (f) => {
    setReassignOpen(false);
    const who = bed.patient_name ? ' and ' + bed.patient_name : '';
    if (!window.confirm('Count ' + bed.bed_code + who + ' under ' + f.org_name + '?\n\nRemoves this bed from this facility census and open-bed counts. Tile stays on this board.')) return;
    try { await axios.patch(API + '/api/beds/' + bed.id + '/census-facility', { census_facility_id: f.id }); onRefresh(); }
    catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };
  const revertCensus = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Return ' + bed.bed_code + ' to this facility census?')) return;
    try { await axios.patch(API + '/api/beds/' + bed.id + '/census-facility', { census_facility_id: null }); onRefresh(); }
    catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
  };
  const occupied = !!bed.patient_name;
  const r = risk || null;
  const loc = bed.episode_loc || bed.loc;
  const lc = locC(loc);
  const daysIn = daysSince(bed.admit_date);
  const authDaysLeft = daysUntil(bed.auth_thru);
  const authExpired = authDaysLeft !== null && authDaysLeft < 0;
  const authExpiring = !authExpired && authDaysLeft !== null && authDaysLeft <= 3;
  const handleSuccess = () => { setModal(null); onRefresh(); };

  return (
    <>
      <div onClick={() => { if (rearrange) { tapBed(bed); return; } setModal(occupied ? 'patient' : (hold ? null : 'admit')); }}
        style={{ border: !occupied && hold ? `2px dashed ${tk.warning}` : `1px solid ${occupied ? tk.borderSoft : tk.borderSoft}`, borderRadius: tk.radiusCard, padding: '10px 12px', background: !occupied && hold ? tk.warningBg : (occupied ? tk.surface : tk.surfaceSoft), minHeight: 84, cursor: 'pointer', transition: 'transform 160ms, box-shadow 160ms', position: 'relative', zIndex: reassignOpen ? 50 : undefined, borderTop: occupied ? `3px solid ${lc.c}` : undefined, borderLeft: occupied && bed.patient_gender ? `4px solid ${bed.patient_gender === 'M' ? tk.genderM : tk.genderF}` : undefined, boxShadow: occupied ? tk.shadow : 'none' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {rearrange && !bed.census_facility_id && (() => {
          const ep = plannedOcc?.[bed.bed_code] || null;
          const pp = ep ? patientsByEp?.[ep] : null;
          const isSel = ep && selectedEp === ep;
          const changed = (ep || null) !== (bed.episode_id || null);
          return (
            <div style={{ position: 'absolute', inset: 0, borderRadius: tk.radiusControl, zIndex: 10, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 6, textAlign: 'center', background: isSel ? tk.warningBg : (changed ? tk.successBg : 'rgba(255,255,255,0.94)'), border: isSel ? `2px solid ${tk.warning}` : (changed ? `2px solid ${tk.success}` : `1px solid ${tk.borderSoft}`) }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted }}>{bed.bed_label}{bed.gender ? ' - ' + bed.gender : ''}</div>
              {pp ? (
                <div style={{ fontSize: 12.5, fontWeight: 700, color: tk.ink }}>
                  {pp.patient_name}
                  <span style={{ marginLeft: 4, color: pp.patient_gender === 'M' ? tk.genderM : tk.genderF }}>{pp.patient_gender || ''}</span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: hold ? tk.warning : tk.success, fontWeight: 700 }}>{hold ? 'HELD' : 'Open'}</div>
              )}
              {isSel && <div style={{ fontSize: 10, fontWeight: 700, color: tk.warning }}>MOVING - tap a bed</div>}
              {changed && !isSel && <div style={{ fontSize: 9.5, fontWeight: 700, color: tk.success }}>changed</div>}
            </div>
          );
        })()}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: tk.textMuted, letterSpacing: '0.05em' }}>{bed.bed_label}</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {bed.census_facility_id ? (
              <span onClick={revertCensus} title={'Counted under ' + (bed.census_org_name || 'other facility') + '. Click to return.'}
                style={{ fontSize: 10, fontWeight: 700, background: tk.blue700, color: '#fff', padding: '1px 6px', borderRadius: 8, cursor: 'pointer' }}>
                {(bed.census_org_name || '?').split(' ')[0].toUpperCase()}
              </span>
            ) : (
              <span onClick={e => { e.stopPropagation(); setReassignOpen(o => !o); }} title="Count this bed under another facility"
                style={{ fontSize: 12, color: tk.blue400, cursor: 'pointer', fontWeight: 700, lineHeight: 1 }}>{SWAP}</span>
            )}
            {occupied && <LocChip loc={loc} />}
          </div>
        </div>
        {reassignOpen && (
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 24, right: 6, zIndex: 20, background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, boxShadow: '0 8px 24px rgba(10,34,57,0.15)', padding: 4, minWidth: 170 }}>
            {(facilities || []).filter(f => f.id !== facilityId).map(f => (
              <div key={f.id} onClick={() => assignCensus(f)}
                style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, color: tk.ink, cursor: 'pointer', borderRadius: 6 }}
                onMouseEnter={e => e.currentTarget.style.background = tk.surfaceSoft}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Count under {f.org_name}
              </div>
            ))}
          </div>
        )}
        {occupied ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: tk.ink, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bed.patient_name}</div>
            <div style={{ fontSize: 11, color: tk.textSecondary, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bed.primary_payer || DASH}</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
              {daysIn !== null && <span style={{ fontSize: 10, fontWeight: 700, background: tk.surfaceSoft, color: tk.textSecondary, padding: '1px 5px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>Day {daysIn}</span>}
              {r && AUTH_BADGE[r.status] && (
                <span title={r.why || ''} style={{ fontSize: 10, fontWeight: 700, background: AUTH_BADGE[r.status].bg, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>
                  {AUTH_BADGE[r.status].label}
                </span>
              )}
              {roundOverdue != null && (
                <span title="Observation round overdue" style={{ fontSize: 10, fontWeight: 700, background: tk.danger, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>
                  ROUND {roundOverdue}m
                </span>
              )}
              {canSeeDollars && r && r.dollars_at_risk > 0 && (
                <span title={`${r.unauthorized_days} unauthorized days`} style={{ fontSize: 10, fontWeight: 700, background: tk.danger, color: '#fff', padding: '1px 5px', borderRadius: 6, fontVariantNumeric: 'tabular-nums' }}>
                  ${Math.round(r.dollars_at_risk / 1000)}K
                </span>
              )}
              {!r && authExpired && <span style={{ fontSize: 10, fontWeight: 700, background: tk.danger, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>AUTH EXPIRED</span>}
              {!r && authExpiring && <span style={{ fontSize: 10, fontWeight: 700, background: tk.warning, color: '#fff', padding: '1px 5px', borderRadius: 6 }}>{WARN} {authDaysLeft}d</span>}
            </div>
            {bed.notes && <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4, fontStyle: 'italic', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>{bed.notes}</div>}
          </>
        ) : hold ? (
          <div onClick={e => e.stopPropagation()} style={{ marginTop: 3 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, fontStyle: 'italic', color: tk.warning, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>({hold.patient_name})</div>
            <div style={{ fontSize: 10, color: tk.warning, fontWeight: holdOverdue ? 700 : 600 }}>
              {holdOverdue ? WARN + ' held ' + holdHours + 'h' : (hold.transfer_departed_at ? 'in transit' : 'arriving') + (hold.expected_admit_date ? ' ' + formatDate(hold.expected_admit_date) : '')}</div>
            <button onClick={markArrived}
              style={{ width: '100%', marginTop: 6, padding: '5px 0', background: tk.success, color: '#fff', border: 'none', borderRadius: tk.radiusControl, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              {CHECK} Arrived</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: tk.ai, fontWeight: 600, marginTop: 10, textAlign: 'center' }}>+ Admit</div>
            {canFlipStaff && !rearrange && <div onClick={markStaff} style={{ fontSize: 10.5, color: tk.textMuted, marginTop: 6, textAlign: 'center', textDecoration: 'underline', cursor: 'pointer' }}>Set aside for staff</div>}
          </>
        )}
      </div>
      {modal === 'admit' && <AdmitModal bed={bed} facilityId={facilityId} onClose={() => setModal(null)} onSuccess={handleSuccess} />}
      {modal === 'arrive' && hold && <AssignBedModal pending={hold} beds={[bed]} facilityId={facilityId} onClose={() => setModal(null)} onSuccess={handleSuccess} />}
      {modal === 'patient' && <PatientModal bed={bed} facilityId={facilityId} allBeds={allBeds} onClose={() => setModal(null)} onSuccess={handleSuccess} />}
    </>
  );
}

// ─── HOUSE SECTION — collapsible, per-LOC splits, auth rollup, occupancy bar ───
function HouseSection({ houseName, beds, allBeds, facilityId, onRefresh, risk, overdueRounds, facilities, holds, rearrange, plannedOcc, selectedEp, patientsByEp, tapBed, canSeeDollars, isVirtual, collapsedDefault }) {
  const [collapsed, setCollapsed] = useState(!!collapsedDefault);
  useEffect(() => { if (rearrange) setCollapsed(false); }, [rearrange]);
  const occupied = beds.filter(b => b.patient_name).length;
  const total = beds.filter(b => b.bed_type !== 'staff').length;
  // Per-LOC splits from bed rows (mixed-LOC houses like Anzar/Michelle render both)
  const locSplit = {};
  beds.forEach(b => { const l = b.loc || '?'; locSplit[l] = (locSplit[l] || 0) + 1; });
  // Auth rollup from risk map
  let authIssues = 0, houseRisk = 0;
  beds.forEach(b => {
    const rr = b.episode_id ? risk?.[b.episode_id] : null;
    if (rr && ACTIONABLE.includes(rr.status)) { authIssues += 1; houseRisk += (rr.dollars_at_risk || 0); }
  });
  const expectedDc = beds.filter(b => { const d = daysUntil(b.expected_dc); return b.patient_name && d !== null && d >= 0 && d <= 2; }).length;
  const sharedFrom = beds[0]?.bed_facility_id && beds[0].bed_facility_id !== facilityId ? beds[0].bed_owner_org : null;
  const domLoc = Object.keys(locSplit).sort((a,b) => locSplit[b] - locSplit[a])[0];
  return (
    <div style={{ marginBottom: 18, background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, overflow: 'hidden' }}>
      <div onClick={() => setCollapsed(c => !c)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 16px', cursor: 'pointer', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
          <Building2 {...IC} color={isVirtual ? tk.textMuted : tk.blue600} />
          <span style={{ fontSize: 14.5, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{houseName}</span>
          {isVirtual && <span style={{ fontSize: 10, fontWeight: 700, color: tk.textMuted, border: `1px solid ${tk.border}`, borderRadius: 6, padding: '1px 6px' }}>VIRTUAL {DOT} not in census</span>}
          {sharedFrom && <span style={{ fontSize: 11, fontWeight: 600, color: tk.blue700 }}>{DOT} shared from {sharedFrom}</span>}
          <span style={{ fontSize: 12.5, fontWeight: 700, color: occupied === total ? tk.ink : tk.success, fontVariantNumeric: 'tabular-nums' }}>{occupied} / {total} occupied</span>
          {Object.entries(locSplit).map(([l, n]) => (
            <span key={l} style={{ fontSize: 11.5, fontWeight: 700, color: locC(l).c }}>{l} {n}</span>
          ))}
          {authIssues > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: tk.danger }}>
              <ShieldAlert {...ICS} color={tk.danger} />
              {canSeeDollars && houseRisk > 0 ? '$' + houseRisk.toLocaleString() + ' auth risk' : authIssues + ' auth issue' + (authIssues === 1 ? '' : 's')}
            </span>
          )}
          {expectedDc > 0 && <span style={{ fontSize: 11.5, fontWeight: 600, color: tk.textSecondary }}>{expectedDc} expected DC</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, justifyContent: 'flex-end', minWidth: 120 }}>
          <OccBar occ={occupied} total={total} color={locC(domLoc).c} />
          <span style={{ fontSize: 12, fontWeight: 700, color: tk.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{occupied}/{total}</span>
          {collapsed ? <ChevronRight {...IC} color={tk.textMuted} /> : <ChevronDown {...IC} color={tk.textMuted} />}
        </div>
      </div>
      {!collapsed && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, padding: '4px 16px 14px' }}>
          {beds.map(bed => (bed.bed_type === 'staff' && !bed.patient_name) ? <StaffBedTile key={bed.bed_code} bed={bed} onRefresh={onRefresh} /> : <BedCard key={bed.bed_code} bed={bed} allBeds={allBeds} facilityId={facilityId} onRefresh={onRefresh} risk={bed.episode_id ? risk?.[bed.episode_id] : null} roundOverdue={overdueRounds?.[bed.bed_code]} facilities={facilities} hold={holds?.[bed.bed_code]} rearrange={rearrange} plannedOcc={plannedOcc} selectedEp={selectedEp} patientsByEp={patientsByEp} tapBed={tapBed} canSeeDollars={canSeeDollars} />)}
        </div>
      )}
    </div>
  );
}

// ─── MAIN BED BOARD ───────────────────────────────────────────
export default function BedBoard({ user }) {
  const isMobile = useIsMobile();
  const token = user?.token || localStorage.getItem('tcio_token');
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [beds, setBeds] = useState([]);
  const [bedSearch, setBedSearch] = useState('');
  const [risk, setRisk] = useState({});
  const [riskSummary, setRiskSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [locFilter, setLocFilter] = useState('ALL');
  const [openOnly, setOpenOnly] = useState(false);
  const [authOnly, setAuthOnly] = useState(false);
  const [view, setView] = useState('board');
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/facilities`).then(res => {
      const facs = res.data.data || [];
      setFacilities(facs);
      const hbts = facs.find(f => f.org_name.includes('Hope')) || facs[0];
      if (hbts) setSelectedFacility(hbts);
    });
  }, []);

  const [overdueRounds, setOverdueRounds] = useState({});
  const loadSeq = useRef(0); // numbers each load so a stale one can be recognized (ticket 4cd27cd4)
  const fetchBeds = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    const seq = ++loadSeq.current;
    // Parallel fetches — page waits for the slower one, not the sum (verbatim pattern)
    const bedsReq = axios.get(`${API}/api/beds/${selectedFacility.id}`)
      .catch(e => { console.error('Failed to load beds:', e); return null; });
    const riskReq = axios.get(`${API}/api/ur/risk?facilityId=${selectedFacility.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(e => { console.error('Failed to load auth risk:', e); return null; });
    const roundsReq = axios.get(`${API}/api/rounds/overdue?facilityId=${selectedFacility.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(e => { console.error('Failed to load overdue rounds:', e); return null; });
    const [bedsRes, riskRes, roundsRes] = await Promise.all([bedsReq, riskReq, roundsReq]);
    if ((seq === loadSeq.current) === false) return; // another facility was picked while this one was loading: drop this answer
    if (roundsRes) {
      const m = {};
      (roundsRes.data.data || []).forEach(x => { if (x.bed_id) m[x.bed_id] = x.overdue_minutes; });
      setOverdueRounds(m);
    }
    if (bedsRes) { setBeds(bedsRes.data.data || []); setLastRefresh(new Date()); }
    if (riskRes?.data?.ok) {
      const m = {};
      riskRes.data.data.forEach(p => { m[p.episode_id] = p; });
      setRisk(m);
      setRiskSummary(riskRes.data.summary || null);
    }
    setLoading(false);
  }, [selectedFacility, token]);
  useEffect(() => { fetchBeds(); }, [fetchBeds]);

  // Dollar visibility derives from the payload: server (commit 53faba1) nulls
  // dollar fields for non-authorized roles. No duplicate role list to drift.
  const canSeeDollars = riskSummary != null && riskSummary.dollars_at_risk !== null && riskSummary.dollars_at_risk !== undefined;
  const riskTotal = canSeeDollars ? (riskSummary.dollars_at_risk || 0) : 0;
  const authIssuesTotal = riskSummary?.auth_issues ?? Object.values(risk).filter(p => ACTIONABLE.includes(p.status)).length;

  const [holds, setHolds] = useState({});
  const holdSeq = useRef(0); // same guard as the bed loader
  const fetchHolds = useCallback(async () => {
    if (!selectedFacility?.id) return;
    try {
      const hseq = ++holdSeq.current;
      const r = await axios.get(`${API}/api/pending-admits?facilityId=${selectedFacility.id}`);
      if ((hseq === holdSeq.current) === false) return; // a newer facility was picked: drop this answer
      const rows = r.data.data || [];
      setPendingCount(rows.length);
      const m = {};
      rows.forEach(pa => { if (pa.held_bed_code) m[pa.held_bed_code] = pa; });
      setHolds(m);
    } catch (e) { console.error(e); }
  }, [selectedFacility]);
  useEffect(() => { fetchHolds(); }, [fetchHolds]);
  const refreshAll = () => { fetchBeds(); fetchHolds(); try { window.dispatchEvent(new Event('tcio:pending-changed')); } catch (e) { /* nothing to notify */ } };

  // -- REARRANGE MODE (verbatim: tray model, atomic apply, gender warn+override) --
  const [rearrange, setRearrange] = useState(false);
  const [assign, setAssign] = useState({});
  const [origAssign, setOrigAssign] = useState({});
  const [selectedEp, setSelectedEp] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [pendingWarnings, setPendingWarnings] = useState(null);
  const patientsByEp = {};
  beds.forEach(b => { if (b.episode_id) patientsByEp[b.episode_id] = b; });
  const startRearrange = () => {
    const a = {};
    beds.forEach(b => { if (b.episode_id && !b.census_facility_id) a[b.episode_id] = b.bed_code; });
    setAssign(a); setOrigAssign(a); setSelectedEp(null); setPendingWarnings(null); setRearrange(true);
  };
  const cancelRearrange = () => { setRearrange(false); setAssign({}); setSelectedEp(null); setPendingWarnings(null); };
  const plannedOcc = {};
  Object.entries(assign).forEach(([ep, code]) => { if (code) plannedOcc[code] = ep; });
  const tray = Object.entries(assign).filter(([, code]) => !code).map(([ep]) => patientsByEp[ep]).filter(Boolean);
  const tapBed = (bed) => {
    if (!rearrange) return false;
    if (bed.census_facility_id) return true;
    if (holds[bed.bed_code] && !plannedOcc[bed.bed_code]) return true;
    const occEp = plannedOcc[bed.bed_code] || null;
    if (!selectedEp) { if (occEp) setSelectedEp(occEp); return true; }
    if (occEp === selectedEp) { setSelectedEp(null); return true; }
    setAssign(prev => {
      const next = { ...prev, [selectedEp]: bed.bed_code };
      if (occEp) next[occEp] = null;
      return next;
    });
    setSelectedEp(null);
    return true;
  };
  const moves = Object.entries(assign)
    .filter(([ep, code]) => code && code !== origAssign[ep])
    .map(([ep, code]) => ({ episode_id: ep, target_bed_code: code }));
  const applyRearrange = async (confirm) => {
    if (tray.length || !moves.length) return;
    setApplyBusy(true);
    try {
      const r = await axios.post(`${API}/api/beds/rearrange`, {
        facility_id: selectedFacility.id, moves, confirm_warnings: !!confirm });
      if (r.data.ok) { cancelRearrange(); refreshAll(); }
      else if (r.data.needs_confirmation) setPendingWarnings(r.data.warnings);
      else alert(r.data.error || 'Apply failed');
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setApplyBusy(false);
  };

  // -- CENSUS MATH (virtual excluded per Chad ruling 2026-09-01) --
  const viewFid = selectedFacility?.id;
  const isVirtualBed = (b) => b.house_type === 'program';
  const nativeBeds = beds.filter(b => !b.census_facility_id || b.census_facility_id === viewFid);
  const reassignedBeds = beds.filter(b => b.census_facility_id && b.census_facility_id !== viewFid);
  const reassignedNames = [...new Set(reassignedBeds.map(b => (b.census_org_name || '?').split(' ')[0]))].join(', ');
  const countable = nativeBeds.filter(b => !isVirtualBed(b) && b.bed_type !== 'staff'); // staff beds never count
  const totalBeds = countable.length;
  const occupiedBeds = countable.filter(b => b.patient_name).length;
  const openBeds = totalBeds - occupiedBeds;
  const openByLoc = {};
  countable.forEach(b => { if (!b.patient_name) { const l = b.loc || '?'; openByLoc[l] = (openByLoc[l] || 0) + 1; } });
  const dc48 = countable.filter(b => { const d = daysUntil(b.expected_dc); return b.patient_name && d !== null && d >= 0 && d <= 2; }).length;

  // -- FILTERS (suspended during rearrange so tap-to-move sees every bed) --
  const bedHasAuthIssue = (b) => {
    if (!b.patient_name) return false;
    const rr = b.episode_id ? risk[b.episode_id] : null;
    if (rr) return ACTIONABLE.includes(rr.status);
    const d = daysUntil(b.auth_thru);
    return d !== null && d < 0;
  };
  const filtersActive = !rearrange && (bedSearch || locFilter !== 'ALL' || openOnly || authOnly);
  const shownBeds = !filtersActive ? beds : beds.filter(b => {
    if (bedSearch && !wordsMatch((b.patient_name || '') + ' ' + (b.bed_code || ''), bedSearch)) return false;
    if (locFilter !== 'ALL' && (b.loc || '') !== locFilter) return false;
    if (openOnly && b.patient_name) return false;
    if (authOnly && !bedHasAuthIssue(b)) return false;
    return true;
  });
  const byHouse = shownBeds.reduce((acc, bed) => {
    if (!acc[bed.house_name]) acc[bed.house_name] = [];
    acc[bed.house_name].push(bed);
    return acc;
  }, {});
  const sortedHouses = Object.keys(byHouse).sort((a, b) => {
    // Virtual houses last (Chad ruling), shared houses after own, then DB house sort_order
    const aV = isVirtualBed(byHouse[a][0]); const bV = isVirtualBed(byHouse[b][0]);
    if (aV !== bV) return aV ? 1 : -1;
    const aShared = byHouse[a][0]?.bed_facility_id && byHouse[a][0].bed_facility_id !== viewFid;
    const bShared = byHouse[b][0]?.bed_facility_id && byHouse[b][0].bed_facility_id !== viewFid;
    if (aShared !== bShared) return aShared ? 1 : -1;
    const ao = byHouse[a][0] ? byHouse[a][0].house_sort_order : null;
    const bo = byHouse[b][0] ? byHouse[b][0].house_sort_order : null;
    if (ao != null && bo != null && ao !== bo) return ao - bo;
    if (ao != null && bo == null) return -1;
    if (ao == null && bo != null) return 1;
    return a.localeCompare(b);
  });

  const locFilterChip = (l) => {
    const active = locFilter === l;
    const c = l === 'ALL' ? tk.ai : locC(l).c;
    return (
      <button key={l} onClick={() => setLocFilter(active && l !== 'ALL' ? 'ALL' : l)}
        style={{ padding: '5px 12px', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 600, fontFamily: 'inherit',
          background: active ? c : tk.surface, color: active ? '#fff' : c, border: `1px solid ${active ? c : tk.borderSoft}` }}>
        {l === 'ALL' ? 'All LOC' : l}
      </button>
    );
  };

  // -- LIST VIEW rows: occupied countable beds + filters applied --
  const listRows = shownBeds.filter(b => b.patient_name);

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 32px', maxWidth: 1440, margin: '0 auto', fontFamily: tk.font, background: tk.canvas, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: '0 0 2px', fontSize: 24, fontWeight: 700, color: tk.ink }}>Bed Board</h2>
          <div style={{ fontSize: 13, color: tk.textSecondary }}>
            {occupiedBeds} occupied {DOT} {openBeds} open {DOT} {totalBeds} beds
            {lastRefresh && <span style={{ color: tk.textMuted }}> {DOT} updated {lastRefresh.toLocaleTimeString()}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedFacility?.id || ''} onChange={e => { const fac = facilities.find(f => f.id === e.target.value); setBeds([]); setRisk({}); setRiskSummary(null); setOverdueRounds({}); setSelectedFacility(fac); }} style={styles.select}>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
          </select>
          <button onClick={() => rearrange ? cancelRearrange() : startRearrange()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: rearrange ? tk.warning : tk.surface, color: rearrange ? '#fff' : tk.blue700, border: rearrange ? 'none' : `1px solid ${tk.blue600}`, borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
            <Move {...ICS} color={rearrange ? '#fff' : tk.blue700} />{rearrange ? 'Exit Rearrange' : 'Rearrange'}</button>
          <button onClick={refreshAll} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: tk.ai, color: '#fff', border: 'none', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
            <RefreshCw {...ICS} color="#fff" />Refresh</button>
          <div style={{ display: 'flex', border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, overflow: 'hidden' }}>
            <button onClick={() => setView('board')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: view === 'board' ? tk.aiSoft : tk.surface, color: view === 'board' ? tk.ai : tk.textSecondary }}><LayoutGrid {...ICS} color={view === 'board' ? tk.ai : tk.textSecondary} />Board</button>
            <button onClick={() => setView('list')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', border: 'none', borderLeft: `1px solid ${tk.border}`, background: view === 'list' ? tk.aiSoft : tk.surface, color: view === 'list' ? tk.ai : tk.textSecondary }}><ListIcon {...ICS} color={view === 'list' ? tk.ai : tk.textSecondary} />List</button>
          </div>
        </div>
      </div>

      {/* Facility command strip */}
      <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <StatTile icon={<BedDouble {...IC} color={tk.blue600} />} label="Occupied" value={`${occupiedBeds} / ${totalBeds}`} />
        <StatTile icon={<DoorOpen {...IC} color={tk.success} />} label="Open Beds" value={openBeds} accent={tk.success}
          sub={LOCS.filter(l => openByLoc[l]).map(l => `${l} ${openByLoc[l]}`).join(' ' + DOT + ' ') || 'none'}
          onClick={() => setOpenOnly(o => !o)} active={openOnly} />
        <StatTile icon={<UserPlus {...IC} color={tk.purple} />} label="Pending Admits" value={pendingCount} accent={tk.purple} />
        {reassignedBeds.length > 0 && (
          <StatTile icon={<ArrowLeftRight {...IC} color={tk.blue700} />} label={`Beds ${ARROW} ${reassignedNames || 'other'}`} value={reassignedBeds.length} accent={tk.blue700} />
        )}
        <StatTile icon={<LogOut {...IC} color={tk.teal} />} label="Expected DC (48h)" value={dc48} accent={tk.teal} sub="expected, not guaranteed" />
        <StatTile icon={<ShieldAlert {...IC} color={authIssuesTotal > 0 ? tk.danger : tk.textMuted} />}
          label={canSeeDollars ? 'Auth Risk' : 'Auth Issues'}
          value={canSeeDollars ? '$' + riskTotal.toLocaleString() : authIssuesTotal}
          accent={authIssuesTotal > 0 ? tk.danger : tk.ink}
          sub={canSeeDollars && authIssuesTotal > 0 ? authIssuesTotal + ' patients' : undefined} subColor={tk.danger}
          onClick={() => setAuthOnly(a => !a)} active={authOnly} />
      </div>

      {/* Filter row */}
      {!rearrange && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {['ALL', ...LOCS].map(locFilterChip)}
          <button onClick={() => setOpenOnly(o => !o)} style={{ padding: '5px 12px', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: openOnly ? 700 : 600, fontFamily: 'inherit', background: openOnly ? tk.success : tk.surface, color: openOnly ? '#fff' : tk.success, border: `1px solid ${openOnly ? tk.success : tk.borderSoft}` }}>Open Beds</button>
          <button onClick={() => setAuthOnly(a => !a)} style={{ padding: '5px 12px', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: authOnly ? 700 : 600, fontFamily: 'inherit', background: authOnly ? tk.danger : tk.surface, color: authOnly ? '#fff' : tk.danger, border: `1px solid ${authOnly ? tk.danger : tk.borderSoft}` }}>Auth Issues</button>
          <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 420 }}>
            <Search {...ICS} color={tk.textMuted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input placeholder={'Search patient or bed' + ELLIP} value={bedSearch} onChange={e => setBedSearch(e.target.value)}
              style={{ ...styles.input, paddingLeft: 34, fontSize: 13 }} />
          </div>
        </div>
      )}

      {/* Pending Admits + Opening Soon */}
      {selectedFacility && (
        <PendingAdmitsSection facilityId={selectedFacility.id} emrLive={!!selectedFacility.emr_live} beds={beds} onRefresh={refreshAll} canSeeDollars={canSeeDollars} />
      )}

      {/* Rearrange bar (verbatim flow, tokens) */}
      {rearrange && (
        <div style={{ position: 'sticky', top: 0, zIndex: 100, background: tk.warningBg, border: `2px solid ${tk.warning}`, borderRadius: tk.radiusCard, padding: '10px 14px', marginBottom: 14, boxShadow: tk.shadow }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: tray.length ? tk.warning : tk.success }}>
              {tray.length ? `To Be Placed - ${tray.length}` : (moves.length ? 'All placed - ready to apply' : 'Tap a patient, then tap their new bed. Tapping an occupied bed displaces its patient here.')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={cancelRearrange} style={{ padding: '7px 14px', background: tk.surface, color: tk.textSecondary, border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={() => applyRearrange(false)} disabled={!!tray.length || !moves.length || applyBusy}
                style={{ padding: '7px 14px', background: (!tray.length && moves.length) ? tk.success : tk.border, color: '#fff', border: 'none', borderRadius: tk.radiusControl, cursor: (!tray.length && moves.length) ? 'pointer' : 'not-allowed', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}>
                {applyBusy ? 'Applying...' : `Apply ${moves.length} move${moves.length === 1 ? '' : 's'}`}</button>
            </div>
          </div>
          {tray.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {tray.map(pt => (
                <div key={pt.episode_id} onClick={() => setSelectedEp(selectedEp === pt.episode_id ? null : pt.episode_id)}
                  style={{ padding: '6px 10px', borderRadius: tk.radiusControl, cursor: 'pointer', border: selectedEp === pt.episode_id ? `2px solid ${tk.warning}` : `2px dashed ${tk.warning}`, background: selectedEp === pt.episode_id ? tk.warningBg : tk.surface, fontSize: 12, fontWeight: 700, color: tk.ink }}>
                  {pt.patient_name} <span style={{ color: pt.patient_gender === 'M' ? tk.genderM : tk.genderF, fontWeight: 700 }}>{pt.patient_gender || ''}</span>
                </div>
              ))}
            </div>
          )}
          {pendingWarnings && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: tk.surface, border: `1px solid ${tk.warning}`, borderRadius: tk.radiusControl }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: tk.warning, marginBottom: 4 }}>Gender check - review before applying:</div>
              {pendingWarnings.map((w, i) => <div key={i} style={{ fontSize: 12, color: tk.ink }}>{w.detail}</div>)}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button onClick={() => setPendingWarnings(null)} style={{ padding: '5px 12px', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: tk.textSecondary, fontFamily: 'inherit' }}>Go back</button>
                <button onClick={() => { setPendingWarnings(null); applyRearrange(true); }} style={{ padding: '5px 12px', background: tk.warning, color: '#fff', border: 'none', borderRadius: tk.radiusControl, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>Apply anyway</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Census */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 0 10px', flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: tk.ink, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Current Census</span>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11.5, fontWeight: 700 }}>
          <span style={{ color: tk.textMuted }}>LOC KEY:</span>
          {LOCS.map(l => (
            <span key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: locC(l).c }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: locC(l).c, display: 'inline-block' }} />{l}
            </span>
          ))}
        </span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: tk.textMuted }}>Loading bed board{ELLIP}</div>
      ) : view === 'list' && !rearrange ? (
        <div style={{ background: tk.surface, border: `1px solid ${tk.borderSoft}`, borderRadius: tk.radiusCard, boxShadow: tk.shadow, overflow: 'auto' }}>
          <div style={{ minWidth: 900 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.6fr 0.5fr 0.5fr 1.2fr 0.9fr 0.8fr 1fr', gap: 8, padding: '9px 14px', background: tk.surfaceSoft, fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              <span>Patient</span><span>House</span><span>Bed</span><span>LOC</span><span>Day</span><span>Payer</span><span>Auth Status</span><span>Expected DC</span><span>Therapist</span>
            </div>
            {listRows.map(b => {
              const rr = b.episode_id ? risk[b.episode_id] : null;
              const badge = rr ? AUTH_BADGE[rr.status] : null;
              const d = daysSince(b.admit_date);
              return (
                <div key={b.bed_code} style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.6fr 0.5fr 0.5fr 1.2fr 0.9fr 0.8fr 1fr', gap: 8, alignItems: 'center', padding: '9px 14px', borderTop: `1px solid ${tk.borderSoft}`, fontSize: 12.5, color: tk.ink }}>
                  <span style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.patient_name}</span>
                    {b.patient_gender && <span style={{ fontSize: 10.5, fontWeight: 700, color: b.patient_gender === 'M' ? tk.genderM : tk.genderF }}>{b.patient_gender}</span>}
                  </span>
                  <span style={{ color: tk.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.house_name}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{b.bed_label || b.bed_code}</span>
                  <span><LocChip loc={b.episode_loc || b.loc} /></span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{d !== null ? d : DASH}</span>
                  <span style={{ color: tk.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.primary_payer || DASH}</span>
                  <span>{badge ? <span style={{ fontSize: 10, fontWeight: 700, background: badge.bg, color: '#fff', padding: '1px 6px', borderRadius: 6 }}>{badge.label}</span> : <span style={{ color: tk.textMuted }}>{DASH}</span>}
                    {canSeeDollars && rr && rr.dollars_at_risk > 0 && <span style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 700, color: tk.danger, fontVariantNumeric: 'tabular-nums' }}>${Math.round(rr.dollars_at_risk / 1000)}K</span>}
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDate(b.expected_dc) || DASH}</span>
                  <span style={{ color: tk.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.therapist || DASH}</span>
                </div>
              );
            })}
            {listRows.length === 0 && <div style={{ padding: '16px 14px', fontSize: 13, color: tk.textMuted }}>No occupied beds match.</div>}
          </div>
        </div>
      ) : (
        sortedHouses.map(houseName => {
          const hb = byHouse[houseName];
          const isVirt = isVirtualBed(hb[0]);
          return (
            <HouseSection key={houseName} houseName={houseName} beds={hb} allBeds={beds} facilityId={viewFid} onRefresh={refreshAll} risk={risk} overdueRounds={overdueRounds} facilities={facilities} holds={holds} rearrange={rearrange} plannedOcc={plannedOcc} selectedEp={selectedEp} patientsByEp={patientsByEp} tapBed={tapBed} canSeeDollars={canSeeDollars} isVirtual={isVirt} collapsedDefault={isVirt && !filtersActive} />
          );
        })
      )}
    </div>
  );
}
